import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  TemplateRef,
  ElementRef,
  HostListener
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { marked } from 'marked';
import * as DOMPurifyImport from 'dompurify';
import { SpinnerService } from '@app/shared/spinner';
import {
  HttpCriteria,
  SuperHttpClientProvider
} from '@app/core/http';
import { WaterBalanceService } from '@app/core/water-balance.service';
import { WaterBalanceEtoService } from '@app/core/water-balance-eto.service';
import { UserService } from '@app/core/user.service';
import {
  ModuleAgentChatService,
  ModuleAgentChatResponse,
  ModuleAgentChatRenderBlock,
  ModuleAgentChatTextBlock,
  ModuleAgentChatChartBlock,
  ModuleAgentChatChartData,
  ModuleAgentChatChartMeta,
  ModuleAgentChatTurn
} from '@app/core/module-agent-chat.service';
import {
  AemetStationsService,
  AemetStation
} from '@app/core/aemet-stations.service';
import {
  SiarStationsService,
  SiarProvince,
  SiarStation
} from '@app/core/siar-stations.service';
import { forkJoin, from, of, Subject } from 'rxjs';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  finalize,
  map,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { DecimalPipe } from '@angular/common';

const DOMPurify = (DOMPurifyImport as any).default || (DOMPurifyImport as any);

type PrecipitationSource =
  | 'pluviometer'
  | 'aemet'
  | 'open_meteo'
  | 'nasa'
  | 'siar';
type EtoSource = 'open_meteo' | 'aemet' | 'siar';

interface WeeklyPrecipitation {
  total?: number;
  effective?: number;
  source?: string;
  daily?: Array<{
    date?: string;
    total?: number;
    effective?: number;
    source?: string;
  }>;
}

interface WeeklyKc {
  week: string;
  kc?: number | null;
  kcSourceUsed?: string | null;
  pas?: number | null;
  monthFactor?: number | null;
}

interface WeeklyEto {
  total?: number;
  source?: string;
  method?: string;
  dataQuality?: string;
  diagnostics?: any;
  daily?: Array<{
    date?: string;
    eto?: number;
  }>;
}

interface WeeklyBalanceDaily {
  date?: string;
  precipTotal?: number;
  precipEffective?: number;
  et0?: number;
  kc?: number;
  etc?: number;
  kcSourceUsed?: string | null;
  pas?: number | null;
  monthFactor?: number | null;
  kcInput?: number | null;
  iviaVariantIdUsed?: number | null;
  iviaCropName?: string | null;
}

interface WeeklyBalanceSummary {
  precipTotalWeekly?: number | null;
  precipEffectiveWeekly?: number | null;
  et0Weekly?: number | null;
  etcWeekly?: number | null;
  netNeedMmWeekly?: number | null;
  grossNeedMmWeekly?: number | null;
  litersPerPlantWeekly?: number | null;
  irrigationHoursPerPlantWeekly?: number | null;
}

interface WeeklyBalanceSources {
  precipitation?: string | null;
  et0?: string | null;
  kc?: string | null;
}

interface WeeklyBalanceResponse {
  daily?: WeeklyBalanceDaily[];
  summary?: WeeklyBalanceSummary;
  sources?: WeeklyBalanceSources;
  quality?: string | null;
  irrigationQuality?: string | null;
  requestedRange?: any;
  actualRange?: any;
  shift?: any;
  diagnostics?: any;
}

interface WeeklyReportFeatureIrrigation {
  litersPerTree?: number | null;
  litersPerTreeTotal?: number | null;
  hoursPerTree?: number | null;
  eventsCount?: number | null;
  confidence?: number | null;
}

interface WeeklyReportFeatureComparison {
  basis?: string | null;
  ratio?: number | null;
  diffPct?: number | null;
  hoursDiff?: number | null;
}

interface WeeklyReportFeatureSoil {
  controlMin?: number | null;
  controlAvg?: number | null;
  controlMax?: number | null;
}

interface WeeklyReportFeatureDrainage {
  episodesCount?: number | null;
  durationHours?: number | null;
}

interface WeeklyReportFeatures {
  theoretical?: {
    grossNeedMmWeekly?: number | null;
    litersPerPlantWeekly?: number | null;
    irrigationHoursPerPlantWeekly?: number | null;
  };
  soil?: WeeklyReportFeatureSoil;
  drainage?: WeeklyReportFeatureDrainage;
  realIrrigation?: WeeklyReportFeatureIrrigation;
  comparison?: WeeklyReportFeatureComparison;
}

interface WeeklyReportInsight {
  code?: string;
  severity?: 'critical' | 'warning' | 'info';
  summaryKey?: string;
  evidence?: any;
  confidence?: number | null;
  tags?: string[];
  dimensions?: any;
}

interface WeeklyReportResponse {
  context?: any;
  features?: WeeklyReportFeatures;
  insights?: WeeklyReportInsight[];
  quality?: string | null;
  debug?: any;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  createdAt: string;
  renderBlocks?: ModuleAgentChatRenderBlock[];
  isError?: boolean;
}

interface ChatMetaState {
  simulated: boolean;
  provider: string | null;
  hasCharts: boolean;
  chartsCount: number;
}

@Component({
  standalone: false,
  selector: 'app-balance',
  templateUrl: './balance.component.html',
  styleUrls: ['./balance.component.scss']
})
export class BalanceComponent implements OnInit, OnDestroy, AfterViewInit {
  public form: FormGroup;
  public rangeForm: FormGroup;
  public clients: any[] = [];
  public fincas: any[] = [];
  public filteredFincas: any[] = [];
  public modules: any[] = [];
  public crops: Array<{
    id: number;
    name: string;
    code?: string;
    category?: string;
    displayName?: string;
    iviaVariants?: Array<{ id: number; name: string }>;
    iviaDefaultVariantId?: number | null;
  }> = [];
  public provinces: string[] = [];
  public stations: AemetStation[] = [];
  public provincesLoading: boolean = false;
  public stationsLoading: boolean = false;
  public siarProvinces: SiarProvince[] = [];
  public siarStations: SiarStation[] = [];
  public siarProvincesLoading: boolean = false;
  public siarStationsLoading: boolean = false;
  public hasPluviometer: boolean = false;
  public weeklySummary: WeeklyPrecipitation | null = null;
  public dailyRows: WeeklyPrecipitation['daily'] = [];
  public weeklyPending: boolean = false;
  public weeklyError: string = '';
  public weeklyMissingData: boolean = false;
  public etoSummary: WeeklyEto | null = null;
  public etoDailyRows: WeeklyEto['daily'] = [];
  public etcWeeklyTotal: number | null = null;
  public weeklyKc: WeeklyKc[] = [];
  public combinedDailyRows: Array<{
    date: string | null;
    precip: number | null;
    eto: number | null;
    kc: number | null;
    etc: number | null;
    kcSource?: string | null;
    kcPas?: number | null;
    kcMonthFactor?: number | null;
    kcIviaVariantIdUsed?: number | null;
    kcIviaCropName?: string | null;
  }> = [];
  public grossNeedMmWeekly: number | null = null;
  public litersPerPlantWeekly: number | null = null;
  public irrigationHoursPerPlantWeekly: number | null = null;
  public irrigationQuality: string | null = null;
  public irrigationDiagnostics: any = null;
  public weeklyReport: WeeklyReportResponse | null = null;
  public reportNarrative: Array<{ key: string; params?: any }> = [];
  public operationalSummary: Array<{ key: string; params?: any }> = [];
  @ViewChild('weeklyReportDialog') weeklyReportDialog!: TemplateRef<any>;
  @ViewChild('chatbotDialog') chatbotDialog!: TemplateRef<any>;
  @ViewChild('chatbotScroll') chatbotScroll?: ElementRef<HTMLDivElement>;
  @ViewChild('balanceTabs', { read: ElementRef })
  private balanceTabsRef?: ElementRef<HTMLElement>;
  private weeklyReportDialogRef?: MatDialogRef<any>;
  private chatbotDialogRef?: MatDialogRef<any>;
  public etoError: string = '';
  public etoNotImplemented: boolean = false;
  public etoDebug: boolean = false;
  public modulesLoading: boolean = false;
  public cropsLoading: boolean = false;
  public saving: boolean = false;
  public roleId: number | null = null;

  public selectedStationLabel: string | null = null;
  public selectedSiarStationLabel: string | null = null;
  public selectedEtoAemetLabel: string | null = null;
  public selectedEtoSiarLabel: string | null = null;
  public chatInput: string = '';
  public chatMessages: ChatMessage[] = [];
  public chatSending: boolean = false;
  public chatError: string = '';
  public chatMeta: ChatMetaState | null = null;
  private stationsByProvince = new Map<string, AemetStation[]>();
  private stationByIdema = new Map<string, AemetStation>();
  private stationLookupRunning = false;
  private siarStationsByProvince = new Map<string, SiarStation[]>();
  private siarStationById = new Map<string, SiarStation>();
  private siarStationLookupRunning = false;
  private chatMessageSequence = 0;
  private chatHistoryByModule = new Map<number, ChatMessage[]>();
  private chatMetaByModule = new Map<number, ChatMetaState>();
  private chatMarkdownCache = new Map<string, string>();
  private lastSavedConfig: {
    cropId: number | null;
    dc: number | null;
    df: number | null;
    dp: number | null;
    iviaVariantId: number | null;
    emittersPerPlant: number | null;
    emitterFlowLph: number | null;
    installationEfficiencyEa: number | null;
    parcelCoefficientCp: number | null;
  } = {
    cropId: null,
    dc: null,
    df: null,
    dp: null,
    iviaVariantId: null,
    emittersPerPlant: null,
    emitterFlowLph: null,
    installationEfficiencyEa: null,
    parcelCoefficientCp: null
  };

  public readonly sources: Array<{
    value: PrecipitationSource;
    labelKey: string;
    requiresPluviometer?: boolean;
    needsStation?: boolean;
  }> = [
    {
      value: 'pluviometer',
      labelKey: 'BALANCE.SOURCES.PLUVIOMETER',
      requiresPluviometer: true
    },
    { value: 'aemet', labelKey: 'BALANCE.SOURCES.AEMET', needsStation: true },
    { value: 'open_meteo', labelKey: 'BALANCE.SOURCES.OPEN_METEO' },
    { value: 'siar', labelKey: 'BALANCE.SOURCES.SIAR', needsStation: true }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private $http: SuperHttpClientProvider,
    private spinner: SpinnerService,
    private waterBalanceService: WaterBalanceService,
    private waterBalanceEtoService: WaterBalanceEtoService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private userService: UserService,
    private aemetStationsService: AemetStationsService,
    private siarStationsService: SiarStationsService,
    private decimalPipe: DecimalPipe,
    private dialog: MatDialog,
    private moduleAgentChatService: ModuleAgentChatService
  ) {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);

    this.form = this.fb.group({
      clientId: [null],
      fincaId: [null],
      moduleId: [null, Validators.required],
      cropId: [null],
      iviaVariantId: [null],
      dc: [null, this.optionalPositiveValidator()],
      df: [null, this.optionalPositiveValidator()],
      dp: [null, this.optionalPositiveValidator()],
      emittersPerPlant: [null, this.optionalPositiveValidator(1)],
      emitterFlowLph: [null, this.optionalPositiveValidator(0)],
      installationEfficiencyEa: [null, this.optionalPercentValidator()],
      parcelCoefficientCp: [null, this.optionalPercentValidator()],
      aemetProvince: [null],
      aemetStationId: [null],
      etoAemetProvince: [null],
      etoAemetStationId: [null],
      siarProvince: [null],
      siarStationId: [null],
      etoSiarProvince: [null],
      etoSiarStationId: [null],
      precipitationSource: [
        'open_meteo' as PrecipitationSource,
        Validators.required
      ],
      etoSource: ['open_meteo' as EtoSource, Validators.required]
    });

    this.rangeForm = this.fb.group({
      start: [start],
      end: [end]
    });
  }

  ngOnInit(): void {
    this.handleSourceChanges();
    this.handleEtoSourceChanges();
    this.roleId = this.userService.getRoleId();
    if (this.roleId === null) {
      this.userService.roleId$.pipe(take(1)).subscribe(role => {
        this.roleId = role;
        this.initData();
      });
    } else {
      this.initData();
    }
  }

  ngAfterViewInit(): void {
    this.updateActiveTabClip();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateActiveTabClip();
  }

  public updateActiveTabClip(): void {
    const tabsHost = this.balanceTabsRef?.nativeElement;
    if (!tabsHost) return;

    requestAnimationFrame(() => {
      const header = tabsHost.querySelector(
        '.mat-mdc-tab-header'
      ) as HTMLElement | null;
      const activeTab = tabsHost.querySelector(
        '.mat-mdc-tab.mdc-tab--active'
      ) as HTMLElement | null;

      if (!header || !activeTab) return;

      const headerRect = header.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      const left = Math.max(0, tabRect.left - headerRect.left);
      const width = Math.max(0, tabRect.width);

      tabsHost.style.setProperty('--balance-active-tab-left', `${left}px`);
      tabsHost.style.setProperty('--balance-active-tab-width', `${width}px`);
    });
  }

  ngOnDestroy(): void {
    this.closeWeeklyReport();
    this.closeChatbot();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleSourceChanges(): void {
    this.form
      .get('precipitationSource')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((source: PrecipitationSource) => {
        this.updatePrecipStationValidator(source);
        if (source === 'aemet') {
          this.ensureProvincesLoaded()
            .pipe(take(1))
            .subscribe();
          const currentId = this.form.get('aemetStationId')?.value;
          if (currentId) {
            this.ensureStationSelection(String(currentId));
          }
          this.clearSiarSelection();
          return;
        }
        if (source === 'siar') {
          this.ensureSiarProvincesLoaded()
            .pipe(take(1))
            .subscribe();
          const currentId = this.form.get('siarStationId')?.value;
          if (currentId) {
            this.ensureSiarStationSelection(String(currentId));
          }
          this.clearAemetSelection();
          return;
        }
        this.clearAemetSelection();
        this.clearSiarSelection();
      });
  }

  private handleEtoSourceChanges(): void {
    this.form
      .get('etoSource')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((source: EtoSource) => {
        this.updateEtoStationValidator(source);
        if (source === 'aemet') {
          this.ensureProvincesLoaded()
            .pipe(take(1))
            .subscribe();
          const currentId = this.form.get('etoAemetStationId')?.value;
          if (currentId) {
            this.ensureEtoAemetSelection(String(currentId));
          }
          this.clearEtoSiarSelection();
          return;
        }
        if (source === 'siar') {
          this.ensureSiarProvincesLoaded()
            .pipe(take(1))
            .subscribe();
          const currentId = this.form.get('etoSiarStationId')?.value;
          if (currentId) {
            this.ensureEtoSiarSelection(String(currentId));
          }
          this.clearEtoAemetSelection();
          return;
        }
        this.clearEtoAemetSelection();
        this.clearEtoSiarSelection();
      });
  }

  private initData(): void {
    if (this.isAdmin()) {
      this.loadClients();
    }
    this.loadFincas();
  }

  private loadClients(): void {
    this.$http.default
      .get('/admin/cliente')
      .pipe(
        take(1),
        catchError(() => of([]))
      )
      .subscribe(data => {
        this.clients = Array.isArray(data) ? data : [];
      });
  }

  private loadFincas(): void {
    this.$http.default
      .get('/admin/finca')
      .pipe(
        take(1),
        catchError(() => of([]))
      )
      .subscribe(data => {
        this.fincas = Array.isArray(data) ? data : [];
        this.filteredFincas = this.filterFincas(
          this.form.get('clientId')?.value
        );
        const initialFinca = this.filteredFincas[0]?.idFinca ?? null;
        this.form.patchValue(
          {
            fincaId: initialFinca
          },
          { emitEvent: false }
        );
        this.loadModulesWithHu(initialFinca || undefined);
      });
  }

  private ensureCropsLoaded() {
    if (this.crops.length) {
      return of(this.crops);
    }
    this.cropsLoading = true;
    return this.waterBalanceService.getCrops().pipe(
      tap(crops => {
        this.crops = this.mapCrops(Array.isArray(crops) ? crops : []);
      }),
      finalize(() => {
        this.cropsLoading = false;
      }),
      catchError(() => {
        this.crops = [];
        return of([] as any[]);
      })
    );
  }

  private ensureProvincesLoaded() {
    if (this.provinces.length) {
      return of(this.provinces);
    }
    this.provincesLoading = true;
    return this.aemetStationsService.getProvinces().pipe(
      tap(provinces => {
        this.provinces = provinces || [];
      }),
      finalize(() => {
        this.provincesLoading = false;
      }),
      catchError(() => of([] as string[]))
    );
  }

  private loadStations(province: string) {
    if (!province) {
      this.stations = [];
      return of([] as AemetStation[]);
    }
    if (this.stationsByProvince.has(province)) {
      const cached = this.stationsByProvince.get(province) || [];
      this.stations = cached;
      return of(cached);
    }
    this.stationsLoading = true;
    return this.aemetStationsService.getStationsByProvince(province).pipe(
      tap(stations => {
        this.stationsByProvince.set(province, stations);
        this.stations = stations;
        stations.forEach(st => {
          if (st?.idema) {
            this.stationByIdema.set(String(st.idema), st);
          }
        });
        this.syncSelectedStationLabel();
        this.syncSelectedEtoAemetLabel();
      }),
      finalize(() => {
        this.stationsLoading = false;
      }),
      catchError(() => {
        this.stations = [];
        return of([] as AemetStation[]);
      })
    );
  }

  private clearAemetSelection(): void {
    const etoUsesAemet = this.form.get('etoSource')?.value === 'aemet';
    this.form.patchValue(
      {
        aemetProvince: null,
        aemetStationId: null
      },
      { emitEvent: false }
    );
    this.selectedStationLabel = null;
    if (!etoUsesAemet) {
      this.stations = [];
      this.stationsLoading = false;
    }
  }

  private clearEtoAemetSelection(): void {
    this.form.patchValue(
      {
        etoAemetProvince: null,
        etoAemetStationId: null
      },
      { emitEvent: false }
    );
    this.selectedEtoAemetLabel = null;
  }

  private ensureStationSelection(idema: string): void {
    if (!idema) {
      return;
    }
    this.selectedStationLabel = this.formatStationLabel({
      idema
    } as AemetStation);
    const cached = this.stationByIdema.get(idema);
    if (cached?.province) {
      this.form.patchValue(
        {
          aemetProvince: cached.province,
          aemetStationId: cached.idema
        },
        { emitEvent: false }
      );
      this.loadStations(cached.province)
        .pipe(take(1))
        .subscribe();
      return;
    }
    if (this.stationLookupRunning) {
      return;
    }
    this.stationLookupRunning = true;
    this.ensureProvincesLoaded()
      .pipe(
        take(1),
        switchMap(provinces =>
          from(provinces).pipe(
            concatMap(province =>
              this.loadStations(province).pipe(
                map(stations => {
                  const found = stations.find(
                    st => String(st?.idema) === String(idema)
                  );
                  return found || null;
                })
              )
            ),
            map(found => found || null),
            defaultIfEmpty(null)
          )
        ),
        take(1),
        finalize(() => {
          this.stationLookupRunning = false;
        })
      )
      .subscribe(found => {
        if (!found) {
          return;
        }
        const province = found?.province;
        if (province) {
          this.form.patchValue(
            {
              aemetProvince: province,
              aemetStationId: found.idema
            },
            { emitEvent: false }
          );
          this.loadStations(province)
            .pipe(take(1))
            .subscribe();
        }
      });
  }

  private ensureSiarProvincesLoaded() {
    if (this.siarProvinces.length) {
      return of(this.siarProvinces);
    }
    this.siarProvincesLoading = true;
    return this.siarStationsService.getProvinces().pipe(
      tap(provinces => {
        this.siarProvinces = provinces || [];
      }),
      finalize(() => {
        this.siarProvincesLoading = false;
      }),
      catchError(() => of([] as SiarProvince[]))
    );
  }

  private loadSiarStations(provinceId: string | number) {
    if (!provinceId && provinceId !== 0) {
      this.siarStations = [];
      return of([] as SiarStation[]);
    }
    const key = String(provinceId);
    if (this.siarStationsByProvince.has(key)) {
      const cached = this.siarStationsByProvince.get(key) || [];
      this.siarStations = cached;
      return of(cached);
    }
    this.siarStationsLoading = true;
    return this.siarStationsService.getStationsByProvince(provinceId).pipe(
      tap(stations => {
        this.siarStationsByProvince.set(key, stations);
        this.siarStations = stations;
        stations.forEach(st => {
          if (st?.id !== undefined && st?.id !== null) {
            this.siarStationById.set(String(st.id), st);
          }
        });
        this.syncSelectedSiarStationLabel();
        this.syncSelectedEtoSiarLabel();
      }),
      finalize(() => {
        this.siarStationsLoading = false;
      }),
      catchError(() => {
        this.siarStations = [];
        return of([] as SiarStation[]);
      })
    );
  }

  private clearSiarSelection(): void {
    const etoUsesSiar = this.form.get('etoSource')?.value === 'siar';
    this.form.patchValue(
      {
        siarProvince: null,
        siarStationId: null
      },
      { emitEvent: false }
    );
    this.selectedSiarStationLabel = null;
    if (!etoUsesSiar) {
      this.siarStations = [];
      this.siarStationsLoading = false;
    }
  }

  private clearEtoSiarSelection(): void {
    this.form.patchValue(
      {
        etoSiarProvince: null,
        etoSiarStationId: null
      },
      { emitEvent: false }
    );
    this.selectedEtoSiarLabel = null;
    this.siarStationsLoading = false;
  }

  private ensureSiarStationSelection(stationId: string): void {
    if (!stationId) {
      return;
    }
    this.selectedSiarStationLabel = this.formatSiarStationLabel({
      id: stationId
    } as SiarStation);
    const cached = this.siarStationById.get(stationId);
    if (cached?.provinceId !== undefined && cached?.provinceId !== null) {
      this.form.patchValue(
        {
          siarProvince: cached.provinceId,
          siarStationId: cached.id
        },
        { emitEvent: false }
      );
      this.loadSiarStations(cached.provinceId)
        .pipe(take(1))
        .subscribe();
      return;
    }
    if (this.siarStationLookupRunning) {
      return;
    }
    this.siarStationLookupRunning = true;
    this.ensureSiarProvincesLoaded()
      .pipe(
        take(1),
        switchMap(provinces =>
          from(provinces).pipe(
            concatMap(province =>
              this.loadSiarStations(province.id).pipe(
                map(stations => {
                  const found = stations.find(
                    st => String(st?.id) === String(stationId)
                  );
                  return found || null;
                })
              )
            ),
            map(found => found || null),
            defaultIfEmpty(null)
          )
        ),
        take(1),
        finalize(() => {
          this.siarStationLookupRunning = false;
        })
      )
      .subscribe(found => {
        if (!found) {
          return;
        }
        const provinceId = found?.provinceId ?? found?.province?.id ?? null;
        if (provinceId !== null && provinceId !== undefined) {
          this.form.patchValue(
            {
              siarProvince: provinceId,
              siarStationId: found.id
            },
            { emitEvent: false }
          );
          this.loadSiarStations(provinceId)
            .pipe(take(1))
            .subscribe();
        }
      });
  }

  private ensureEtoSiarSelection(stationId: string): void {
    if (!stationId) {
      return;
    }
    this.selectedEtoSiarLabel = this.formatSiarStationLabel({
      id: stationId
    } as SiarStation);
    const cached = this.siarStationById.get(stationId);
    if (cached?.provinceId !== undefined && cached?.provinceId !== null) {
      this.form.patchValue(
        {
          etoSiarProvince: cached.provinceId,
          etoSiarStationId: cached.id
        },
        { emitEvent: false }
      );
      this.loadSiarStations(cached.provinceId)
        .pipe(take(1))
        .subscribe();
      return;
    }
    if (this.siarStationLookupRunning) {
      return;
    }
    this.siarStationLookupRunning = true;
    this.ensureSiarProvincesLoaded()
      .pipe(
        take(1),
        switchMap(provinces =>
          from(provinces).pipe(
            concatMap(province =>
              this.loadSiarStations(province.id).pipe(
                map(stations => {
                  const found = stations.find(
                    st => String(st?.id) === String(stationId)
                  );
                  return found || null;
                })
              )
            ),
            map(found => found || null),
            defaultIfEmpty(null)
          )
        ),
        take(1),
        finalize(() => {
          this.siarStationLookupRunning = false;
        })
      )
      .subscribe(found => {
        if (!found) {
          return;
        }
        const provinceId = found?.provinceId ?? found?.province?.id ?? null;
        if (provinceId !== null && provinceId !== undefined) {
          this.form.patchValue(
            {
              etoSiarProvince: provinceId,
              etoSiarStationId: found.id
            },
            { emitEvent: false }
          );
          this.loadSiarStations(provinceId)
            .pipe(take(1))
            .subscribe();
        }
      });
  }

  private ensureEtoAemetSelection(idema: string): void {
    if (!idema) {
      return;
    }
    this.selectedEtoAemetLabel = this.formatStationLabel({
      idema
    } as AemetStation);
    const cached = this.stationByIdema.get(idema);
    if (cached?.province) {
      this.form.patchValue(
        {
          etoAemetProvince: cached.province,
          etoAemetStationId: cached.idema
        },
        { emitEvent: false }
      );
      this.loadStations(cached.province)
        .pipe(take(1))
        .subscribe();
      return;
    }
    if (this.stationLookupRunning) {
      return;
    }
    this.stationLookupRunning = true;
    this.ensureProvincesLoaded()
      .pipe(
        take(1),
        switchMap(provinces =>
          from(provinces).pipe(
            concatMap(province =>
              this.loadStations(province).pipe(
                map(stations => {
                  const found = stations.find(
                    st => String(st?.idema) === String(idema)
                  );
                  return found || null;
                })
              )
            ),
            map(found => found || null),
            defaultIfEmpty(null)
          )
        ),
        take(1),
        finalize(() => {
          this.stationLookupRunning = false;
        })
      )
      .subscribe(found => {
        if (!found) {
          return;
        }
        const province = found?.province;
        if (province) {
          this.form.patchValue(
            {
              etoAemetProvince: province,
              etoAemetStationId: found.idema
            },
            { emitEvent: false }
          );
          this.loadStations(province)
            .pipe(take(1))
            .subscribe();
        }
      });
  }

  private filterFincas(clientId: number | null): any[] {
    if (!clientId) return [...this.fincas];
    return this.fincas.filter(f => Number(f?.idCliente) === Number(clientId));
  }

  private loadModulesWithHu(fincaId?: number): void {
    const moduleCriteria = new HttpCriteria({ _perPage: -1, _page: -1 });
    if (fincaId) {
      moduleCriteria.addFilter({
        field: 'idFinca',
        operator: '=',
        value: fincaId
      } as any);
    }
    const huSensorCriteria = new HttpCriteria({
      _perPage: -1
    });

    this.modulesLoading = true;
    this.spinner.show();

    forkJoin({
      modules: this.$http.default
        .filter('/admin/module', moduleCriteria)
        .pipe(take(1)),
      sensors: this.$http.default
        .filter('/admin/sensor', huSensorCriteria)
        .pipe(take(1))
    })
      .pipe(
        map(({ modules, sensors }) =>
          this.filterModulesWithHu(modules, sensors)
        ),
        switchMap(mods => {
          this.modules = mods;
          const firstId = mods.length ? mods[0].idModule : null;
          this.form.patchValue({ moduleId: firstId }, { emitEvent: false });
          this.restoreChatState(firstId);
          if (firstId) {
            return this.loadModuleDetails(firstId, false);
          }
          this.form.patchValue(
            { cropId: null, dc: null, df: null, dp: null },
            { emitEvent: false }
          );
          this.lastSavedConfig = {
            cropId: null,
            dc: null,
            df: null,
            dp: null,
            iviaVariantId: null,
            emittersPerPlant: null,
            emitterFlowLph: null,
            installationEfficiencyEa: null,
            parcelCoefficientCp: null
          };
          this.weeklySummary = null;
          this.dailyRows = [];
          this.weeklyKc = [];
          this.rebuildCombinedDailyRows();
          return of(null);
        }),
        catchError(() => {
          this.snackBar.open(
            this.translate.instant('BALANCE.ERROR_LOAD'),
            this.translate.instant('COMMON.CLOSE'),
            { duration: 4000 }
          );
          return of(null);
        }),
        finalize(() => {
          this.modulesLoading = false;
          this.spinner.hide();
        })
      )
      .subscribe();
  }

  public onClientChange(clientId: number): void {
    const parsed = Number(clientId) || null;
    this.closeChatbot();
    this.form.patchValue(
      {
        clientId: parsed,
        fincaId: null,
        moduleId: null
      },
      { emitEvent: false }
    );
    this.filteredFincas = this.filterFincas(parsed);
    const nextFinca = this.filteredFincas[0]?.idFinca ?? null;
    this.form.patchValue({ fincaId: nextFinca }, { emitEvent: false });
    this.modules = [];
    this.hasPluviometer = false;
    if (nextFinca) {
      this.loadModulesWithHu(nextFinca);
    } else {
      this.loadModulesWithHu();
    }
  }

  public onAemetProvinceChange(province: string): void {
    const parsed = (province || '').toString().trim() || null;
    this.form.patchValue(
      {
        aemetProvince: parsed,
        aemetStationId: null
      },
      { emitEvent: false }
    );
    this.stations = [];
    this.selectedStationLabel = null;
    if (parsed) {
      this.loadStations(parsed)
        .pipe(take(1))
        .subscribe();
    }
  }

  public onAemetStationChange(stationId: string): void {
    const parsed = stationId ? String(stationId) : null;
    this.form.patchValue(
      {
        aemetStationId: parsed
      },
      { emitEvent: false }
    );
    if (parsed) {
      const cached = this.stationByIdema.get(parsed);
      this.selectedStationLabel = cached
        ? this.formatStationLabel(cached)
        : this.formatStationLabel({ idema: parsed } as AemetStation);
    } else {
      this.selectedStationLabel = null;
    }
  }

  public onEtoAemetProvinceChange(province: string): void {
    const parsed = (province || '').toString().trim() || null;
    this.form.patchValue(
      {
        etoAemetProvince: parsed,
        etoAemetStationId: null
      },
      { emitEvent: false }
    );
    this.selectedEtoAemetLabel = null;
    if (parsed) {
      this.loadStations(parsed)
        .pipe(take(1))
        .subscribe();
    }
  }

  public onEtoAemetStationChange(stationId: string): void {
    const parsed = stationId ? String(stationId) : null;
    this.form.patchValue(
      {
        etoAemetStationId: parsed
      },
      { emitEvent: false }
    );
    if (parsed) {
      const cached = this.stationByIdema.get(parsed);
      this.selectedEtoAemetLabel = cached
        ? this.formatStationLabel(cached)
        : this.formatStationLabel({ idema: parsed } as AemetStation);
    } else {
      this.selectedEtoAemetLabel = null;
    }
  }

  public onSiarProvinceChange(provinceId: string | number): void {
    const parsed =
      provinceId === null || provinceId === undefined
        ? null
        : String(provinceId);
    this.form.patchValue(
      {
        siarProvince: parsed,
        siarStationId: null
      },
      { emitEvent: false }
    );
    this.siarStations = [];
    this.selectedSiarStationLabel = null;
    if (parsed !== null) {
      this.loadSiarStations(parsed)
        .pipe(take(1))
        .subscribe();
    }
  }

  public onSiarStationChange(stationId: string | number): void {
    const parsed =
      stationId === null || stationId === undefined ? null : String(stationId);
    this.form.patchValue(
      {
        siarStationId: parsed
      },
      { emitEvent: false }
    );
    if (parsed) {
      const cached = this.siarStationById.get(parsed);
      this.selectedSiarStationLabel = cached
        ? this.formatSiarStationLabel(cached)
        : this.formatSiarStationLabel({ id: parsed } as SiarStation);
    } else {
      this.selectedSiarStationLabel = null;
    }
  }

  public onEtoSiarProvinceChange(provinceId: string | number): void {
    const parsed =
      provinceId === null || provinceId === undefined
        ? null
        : String(provinceId);
    this.form.patchValue(
      {
        etoSiarProvince: parsed,
        etoSiarStationId: null
      },
      { emitEvent: false }
    );
    this.selectedEtoSiarLabel = null;
    if (parsed !== null) {
      this.loadSiarStations(parsed)
        .pipe(take(1))
        .subscribe();
    }
  }

  public onEtoSiarStationChange(stationId: string | number): void {
    const parsed =
      stationId === null || stationId === undefined ? null : String(stationId);
    this.form.patchValue(
      {
        etoSiarStationId: parsed
      },
      { emitEvent: false }
    );
    if (parsed) {
      const cached = this.siarStationById.get(parsed);
      this.selectedEtoSiarLabel = cached
        ? this.formatSiarStationLabel(cached)
        : this.formatSiarStationLabel({ id: parsed } as SiarStation);
    } else {
      this.selectedEtoSiarLabel = null;
    }
  }

  public onFincaChange(fincaId: number): void {
    const parsed = Number(fincaId) || null;
    this.closeChatbot();
    this.form.patchValue({ moduleId: null }, { emitEvent: false });
    this.modules = [];
    this.hasPluviometer = false;
    if (parsed) {
      this.loadModulesWithHu(parsed);
    } else {
      this.loadModulesWithHu();
    }
  }

  public onModuleChange(moduleId: number): void {
    const parsed = Number(moduleId);
    if (!parsed) {
      this.closeChatbot();
      this.restoreChatState(null);
      this.form.patchValue(
        {
          cropId: null,
          iviaVariantId: null,
          dc: null,
          df: null,
          dp: null,
          emittersPerPlant: null,
          emitterFlowLph: null,
          installationEfficiencyEa: null,
          parcelCoefficientCp: null
        },
        { emitEvent: false }
      );
      this.lastSavedConfig = {
        cropId: null,
        dc: null,
        df: null,
        dp: null,
        iviaVariantId: null,
        emittersPerPlant: null,
        emitterFlowLph: null,
        installationEfficiencyEa: null,
        parcelCoefficientCp: null
      };
      this.weeklySummary = null;
      this.dailyRows = [];
      this.weeklyKc = [];
      this.rebuildCombinedDailyRows();
      return;
    }
    this.restoreChatState(parsed);
    this.loadModuleDetails(parsed, true).subscribe();
  }

  public onCropChange(cropId: number | null): void {
    this.form.patchValue({ iviaVariantId: null }, { emitEvent: false });
  }

  private loadModuleDetails(moduleId: number, showSpinner = true) {
    const config$ = this.waterBalanceService.getConfig(moduleId).pipe(
      take(1),
      catchError(() => of(null))
    );

    const sensors$ = this.$http.default
      .filter(
        '/admin/sensor',
        new HttpCriteria({
          _filters: [{ field: 'idModule', operator: '=', value: moduleId }],
          _perPage: -1
        })
      )
      .pipe(
        take(1),
        catchError(() => of([] as any[]))
      );

    const weeklyBalance$ = this.getWeeklyBalanceObservable(moduleId).pipe(
      take(1)
    );
    const weeklyReport$ = this.getWeeklyReportObservable(moduleId).pipe(
      take(1)
    );
    const crops$ = this.ensureCropsLoaded().pipe(take(1));

    const stream$ = forkJoin({
      config: config$,
      sensors: sensors$,
      weeklyBalance: weeklyBalance$,
      weeklyReport: weeklyReport$,
      crops: crops$
    }).pipe(
      tap(({ config, sensors, weeklyBalance, weeklyReport, crops }) => {
        this.crops = this.mapCrops(Array.isArray(crops) ? crops : []);
        this.hasPluviometer = this.hasPluviometerSensor(sensors);
        this.applyConfig(config);
        this.applyWeeklyBalance(weeklyBalance);
        this.applyWeeklyReport(weeklyReport);
        this.enforceSourceAvailability();
      }),
      catchError(() => {
        this.snackBar.open(
          this.translate.instant('BALANCE.ERROR_LOAD'),
          this.translate.instant('COMMON.CLOSE'),
          { duration: 4000 }
        );
        return of(null);
      })
    );

    const final$ = showSpinner
      ? stream$.pipe(finalize(() => this.spinner.hide()))
      : stream$;

    if (showSpinner) {
      this.spinner.show();
    }

    return final$;
  }

  private getWeeklyObservable(moduleId: number) {
    const start = this.formatDate(this.rangeForm.get('start')?.value);
    const end = this.formatDate(this.rangeForm.get('end')?.value);
    this.weeklyPending = false;
    this.weeklyError = '';

    if (!start || !end) {
      return of(null);
    }

    return this.waterBalanceService
      .getWeeklyPrecipitation(moduleId, start, end)
      .pipe(
        catchError(err => {
          if (err?.status === 501) {
            this.weeklyPending = true;
            return of(null);
          }
          if (err?.status === 503) {
            this.weeklyError = this.translate.instant(
              'BALANCE.ERROR_WEEKLY_NASA'
            );
            return of(null);
          }
          this.weeklyError = this.translate.instant('BALANCE.ERROR_WEEKLY');
          return of(null);
        })
      );
  }

  private getWeeklyEtoObservable(moduleId: number) {
    const start = this.formatDate(this.rangeForm.get('start')?.value);
    const end = this.formatDate(this.rangeForm.get('end')?.value);
    this.etoError = '';
    this.etoNotImplemented = false;

    if (!start || !end) {
      return of(null);
    }

    return this.waterBalanceEtoService
      .getWeeklyEto(moduleId, start, end, this.etoDebug)
      .pipe(
        catchError(err => {
          if (err?.status === 501) {
            this.etoNotImplemented = true;
            return of(null);
          }
          if (err?.status === 503) {
            this.etoError = this.translate.instant('BALANCE.ETO_UNAVAILABLE');
            return of(null);
          }
          this.etoError = this.translate.instant('BALANCE.ETO_ERROR');
          return of(null);
        })
      );
  }

  private getWeeklyBalanceObservable(moduleId: number) {
    const start = this.formatDate(this.rangeForm.get('start')?.value);
    const end = this.formatDate(this.rangeForm.get('end')?.value);
    this.weeklyError = '';
    if (!start || !end) {
      return of(null);
    }
    return this.waterBalanceService
      .getWeeklyBalance(moduleId, start, end, this.etoDebug)
      .pipe(
        catchError(() => {
          this.weeklyError = this.translate.instant('BALANCE.ERROR_WEEKLY');
          return of(null);
        })
      );
  }

  private getWeeklyKcObservable(moduleId: number) {
    const start = this.formatDate(this.rangeForm.get('start')?.value);
    const end = this.formatDate(this.rangeForm.get('end')?.value);
    if (!start || !end) {
      return of(null);
    }
    return this.waterBalanceService
      .getWeeklyKc(moduleId, start, end)
      .pipe(catchError(() => of(null)));
  }

  public refreshWeekly(): void {
    const moduleId = Number(this.form.get('moduleId')?.value);
    const start = this.rangeForm.get('start')?.value;
    const end = this.rangeForm.get('end')?.value;
    if (!moduleId || !start || !end) {
      return;
    }

    this.spinner.show();
    forkJoin({
      balance: this.getWeeklyBalanceObservable(moduleId).pipe(take(1)),
      report: this.getWeeklyReportObservable(moduleId).pipe(take(1))
    })
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe(({ balance, report }) => {
        this.applyWeeklyBalance(balance);
        this.applyWeeklyReport(report);
      });
  }

  public onRangeChange(range: { begin: Date | null; end: Date | null }): void {
    this.rangeForm.patchValue({
      start: range?.begin || null,
      end: range?.end || null
    });

    const start = this.rangeForm.get('start')?.value;
    const end = this.rangeForm.get('end')?.value;
    if (start && end && this.form.get('moduleId')?.value) {
      this.refreshWeekly();
    }
  }

  public save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const moduleId = Number(this.form.get('moduleId')?.value);
    const source = this.normalizePrecipitationSource(
      this.form.get('precipitationSource')?.value
    );
    const etoSource = this.form.get('etoSource')?.value as EtoSource;
    const cropIdValue = this.form.get('cropId')?.value;
    const dcValue = this.parsePlantingValue(this.form.get('dc')?.value);
    const dfValue = this.parsePlantingValue(this.form.get('df')?.value);
    const dpValue = this.parsePlantingValue(this.form.get('dp')?.value);
    const iviaVariantIdRaw = this.form.get('iviaVariantId')?.value;
    const aemetStationId = this.form.get('aemetStationId')?.value || null;
    const etoAemetStationId = this.form.get('etoAemetStationId')?.value || null;
    const siarStationId = this.form.get('siarStationId')?.value || null;
    const etoSiarStationId = this.form.get('etoSiarStationId')?.value || null;
    const emittersPerPlant = this.parsePlantingValue(
      this.form.get('emittersPerPlant')?.value
    );
    const emitterFlowLph = this.parsePlantingValue(
      this.form.get('emitterFlowLph')?.value
    );
    const installationEfficiencyEa = this.normalizePercentToSend(
      this.form.get('installationEfficiencyEa')?.value
    );
    const parcelCoefficientCp = this.normalizePercentToSend(
      this.form.get('parcelCoefficientCp')?.value
    );
    if (!moduleId) {
      return;
    }

    const parsedCropId =
      cropIdValue === null || cropIdValue === undefined || cropIdValue === ''
        ? null
        : Number(cropIdValue);
    const cropIdToSend = Number.isFinite(parsedCropId as number)
      ? (parsedCropId as number)
      : null;

    const payload: any = {
      idModule: moduleId,
      precipitationSource: (source || '').toString().toUpperCase(),
      etoSource: (etoSource || '').toString().toUpperCase(),
      cropId: cropIdToSend,
      dc: dcValue,
      df: dfValue,
      dp: dpValue,
      iviaVariantId:
        iviaVariantIdRaw === null ||
        iviaVariantIdRaw === undefined ||
        iviaVariantIdRaw === ''
          ? null
          : Number(iviaVariantIdRaw),
      emittersPerPlant,
      emitterFlowLph,
      installationEfficiencyEa,
      parcelCoefficientCp
    };

    if (source === 'aemet') {
      payload.aemetStationId = aemetStationId;
    }
    if (source === 'siar') {
      payload.siarStationId = siarStationId;
    }
    if (source !== 'aemet' && etoSource === 'aemet') {
      payload.aemetStationId = etoAemetStationId;
    }
    if (source !== 'siar' && etoSource === 'siar') {
      payload.siarStationId = etoSiarStationId;
    }

    this.saving = true;
    this.spinner.show();

    this.waterBalanceService
      .updateConfig(moduleId, payload)
      .pipe(
        take(1),
        tap(() => {
          this.lastSavedConfig = {
            cropId: cropIdToSend ?? null,
            dc: dcValue ?? null,
            df: dfValue ?? null,
            dp: dpValue ?? null,
            iviaVariantId: payload.iviaVariantId ?? null,
            emittersPerPlant: emittersPerPlant ?? null,
            emitterFlowLph: emitterFlowLph ?? null,
            installationEfficiencyEa: installationEfficiencyEa ?? null,
            parcelCoefficientCp: parcelCoefficientCp ?? null
          };
          this.snackBar.open(
            this.translate.instant('BALANCE.SAVE_SUCCESS'),
            this.translate.instant('COMMON.CLOSE'),
            { duration: 3000 }
          );
        }),
        catchError(err => {
          const errorCode = err?.error?.error || err?.error;
          if (errorCode === 'INVALID_CROP') {
            this.restoreLastSavedPlanting();
            this.snackBar.open(
              this.translate.instant('BALANCE.ERROR_INVALID_CROP'),
              this.translate.instant('COMMON.CLOSE'),
              { duration: 4000 }
            );
            return of(null);
          }
          if (
            ['DC_INVALID', 'DF_INVALID', 'DP_INVALID'].includes(
              String(errorCode || '')
            )
          ) {
            this.restoreLastSavedPlanting();
            this.snackBar.open(
              this.translate.instant('BALANCE.ERROR_INVALID_PLANT_PARAMS'),
              this.translate.instant('COMMON.CLOSE'),
              { duration: 4000 }
            );
            return of(null);
          }
          if (errorCode === 'INVALID_IVIA_VARIANT') {
            this.restoreLastSavedPlanting();
            this.snackBar.open(
              this.translate.instant('BALANCE.ERROR_INVALID_IVIA_VARIANT'),
              this.translate.instant('COMMON.CLOSE'),
              { duration: 4000 }
            );
            return of(null);
          }
          this.snackBar.open(
            this.translate.instant('BALANCE.SAVE_ERROR'),
            this.translate.instant('COMMON.CLOSE'),
            { duration: 4000 }
          );
          return of(null);
        }),
        finalize(() => {
          this.saving = false;
          this.spinner.hide();
        })
      )
      .subscribe();
  }

  private applyConfig(config: any): void {
    const rawSource = (config?.precipitationSource as string) || 'open_meteo';
    const source = this.normalizePrecipitationSource(rawSource);
    const rawEtoSource = (config?.etoSource as string) || 'open_meteo';
    const normalizedEtoSource = (rawEtoSource || '')
      .toString()
      .trim()
      .toLowerCase();
    const etoSource = (normalizedEtoSource === 'nasa'
      ? 'open_meteo'
      : normalizedEtoSource) as EtoSource;
    const aemetStationId = config?.aemetStationId ?? config?.stationId ?? null;
    const siarStationId = config?.siarStationId ?? null;
    const dc = this.parsePlantingValue(config?.dc);
    const df = this.parsePlantingValue(config?.df);
    const dp = this.parsePlantingValue(config?.dp);
    const emittersPerPlant = this.parsePlantingValue(config?.emittersPerPlant);
    const emitterFlowLph = this.parsePlantingValue(config?.emitterFlowLph);
    const installationEfficiencyEa = this.normalizePercentFromBackend(
      config?.installationEfficiencyEa
    );
    const parcelCoefficientCp = this.normalizePercentFromBackend(
      config?.parcelCoefficientCp
    );

    this.form.patchValue(
      {
        precipitationSource: source,
        etoSource: etoSource,
        cropId: config?.cropId ?? null,
        iviaVariantId: config?.iviaVariantId ?? null,
        dc: dc,
        df: df,
        dp: dp,
        emittersPerPlant,
        emitterFlowLph,
        installationEfficiencyEa,
        parcelCoefficientCp,
        aemetProvince: null,
        aemetStationId: aemetStationId,
        siarProvince: null,
        siarStationId: siarStationId,
        etoAemetProvince: null,
        etoAemetStationId: aemetStationId,
        etoSiarProvince: null,
        etoSiarStationId: siarStationId
      },
      { emitEvent: false }
    );
    this.lastSavedConfig = {
      cropId: config?.cropId ?? null,
      dc,
      df,
      dp,
      iviaVariantId: config?.iviaVariantId ?? null,
      emittersPerPlant,
      emitterFlowLph,
      installationEfficiencyEa,
      parcelCoefficientCp
    };

    this.updatePrecipStationValidator(source);
    this.updateEtoStationValidator(etoSource);
    if (source === 'aemet' && aemetStationId) {
      this.selectedStationLabel = this.formatStationLabel({
        idema: String(aemetStationId)
      } as AemetStation);
      this.ensureProvincesLoaded()
        .pipe(take(1))
        .subscribe();
      this.ensureStationSelection(String(aemetStationId));
      this.clearSiarSelection();
    }
    if (source === 'siar' && siarStationId) {
      this.selectedSiarStationLabel = this.formatSiarStationLabel({
        id: String(siarStationId)
      } as SiarStation);
      this.ensureSiarProvincesLoaded()
        .pipe(take(1))
        .subscribe();
      this.ensureSiarStationSelection(String(siarStationId));
      this.clearAemetSelection();
    }
    if (etoSource === 'aemet' && aemetStationId) {
      this.selectedEtoAemetLabel = this.formatStationLabel({
        idema: String(aemetStationId)
      } as AemetStation);
      this.ensureProvincesLoaded()
        .pipe(take(1))
        .subscribe();
      this.ensureEtoAemetSelection(String(aemetStationId));
      this.clearEtoSiarSelection();
    }
    if (etoSource === 'siar' && siarStationId) {
      this.selectedEtoSiarLabel = this.formatSiarStationLabel({
        id: String(siarStationId)
      } as SiarStation);
      this.ensureSiarProvincesLoaded()
        .pipe(take(1))
        .subscribe();
      this.ensureEtoSiarSelection(String(siarStationId));
      this.clearEtoAemetSelection();
    }
    if (source !== 'aemet') {
      this.clearAemetSelection();
    }
    if (source !== 'siar') {
      this.clearSiarSelection();
    }
    if (etoSource !== 'aemet') {
      this.clearEtoAemetSelection();
    }
    if (etoSource !== 'siar') {
      this.clearEtoSiarSelection();
    }
  }

  private restoreLastSavedPlanting(): void {
    this.form.patchValue(
      {
        cropId: this.lastSavedConfig.cropId,
        iviaVariantId: this.lastSavedConfig.iviaVariantId,
        dc: this.lastSavedConfig.dc,
        df: this.lastSavedConfig.df,
        dp: this.lastSavedConfig.dp,
        emittersPerPlant: this.lastSavedConfig.emittersPerPlant,
        emitterFlowLph: this.lastSavedConfig.emitterFlowLph,
        installationEfficiencyEa: this.lastSavedConfig.installationEfficiencyEa,
        parcelCoefficientCp: this.lastSavedConfig.parcelCoefficientCp
      },
      { emitEvent: false }
    );
  }

  private applySummary(summary: WeeklyPrecipitation | null): void {
    if (!summary) {
      this.weeklySummary = null;
      this.dailyRows = [];
      this.weeklyMissingData = false;
      this.rebuildCombinedDailyRows();
      return;
    }

    const source =
      (summary as any).source ??
      (summary as any).sourceUsed ??
      (summary as any).sourceLabel ??
      null;

    let sourceLabel: string | null = source;
    const normalizedSource = this.normalizePrecipitationSource(source);
    if (normalizedSource === 'open_meteo') {
      sourceLabel = this.translate.instant('BALANCE.SOURCES.OPEN_METEO');
    }

    const normalizeNumber = (value: any): number | null => {
      if (value === null || value === undefined) return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    const rawTotal =
      normalizeNumber((summary as any).total) ??
      normalizeNumber((summary as any).totalMm) ??
      null;
    const rawEffective =
      normalizeNumber((summary as any).effective) ??
      normalizeNumber((summary as any).effectiveMm) ??
      null;
    const dailyRaw = Array.isArray((summary as any).daily)
      ? (summary as any).daily
      : [];
    const allDailyMissing =
      dailyRaw.length > 0 &&
      dailyRaw.every(
        (day: any) =>
          normalizeNumber(day?.total) === null &&
          normalizeNumber(day?.precipMm) === null &&
          normalizeNumber(day?.effective) === null &&
          normalizeNumber(day?.effectiveMm) === null &&
          normalizeNumber(day?.precipEffectiveMm) === null
      );
    const dataQuality = String((summary as any).dataQuality || '')
      .trim()
      .toLowerCase();
    this.weeklyMissingData =
      dataQuality === 'missing' ||
      (allDailyMissing && rawTotal === 0 && rawEffective === 0);

    const normalizedDaily =
      Array.isArray((summary as any).daily) && (summary as any).daily.length
        ? (summary as any).daily.map((day: any) => {
            return {
              date: day?.date || day?.day || null,
              total:
                normalizeNumber(day?.total) ??
                normalizeNumber(day?.precipMm) ??
                null,
              effective:
                normalizeNumber(day?.effective) ??
                normalizeNumber(day?.effectiveMm) ??
                normalizeNumber(day?.precipEffectiveMm) ??
                null,
              source: day?.source || sourceLabel || null
            };
          })
        : [];

    this.weeklySummary = {
      total: rawTotal,
      effective: rawEffective,
      source: sourceLabel,
      daily: normalizedDaily
    };
    this.dailyRows = normalizedDaily;
    this.rebuildCombinedDailyRows();
  }

  private applyEtoSummary(summary: WeeklyEto | null): void {
    if (!summary) {
      this.etoSummary = null;
      this.etoDailyRows = [];
      this.rebuildCombinedDailyRows();
      return;
    }

    const normalizeNumber = (value: any): number | null => {
      if (value === null || value === undefined) return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    let source = (summary as any).source ?? (summary as any).sourceUsed ?? null;
    const method = (summary as any).method || null;
    const dataQuality =
      String((summary as any).dataQuality || '')
        .trim()
        .toLowerCase() || null;

    const dailyRaw = Array.isArray((summary as any).daily)
      ? (summary as any).daily
      : [];
    const filteredDaily = dailyRaw.filter((day: any) =>
      this.isWithinSelectedRange(day?.date || day?.day)
    );
    const normalizedDaily = filteredDaily.map((day: any) => ({
      date: day?.date || day?.day || null,
      eto: normalizeNumber(day?.eto) ?? normalizeNumber(day?.etoMm) ?? null
    }));

    if (String(source || '').toLowerCase() === 'open_meteo') {
      source = this.translate.instant('BALANCE.SOURCES.OPEN_METEO');
    }

    const totalFromFiltered = normalizedDaily
      .map(d => d.eto)
      .filter(
        (v): v is number => v !== null && v !== undefined && Number.isFinite(v)
      )
      .reduce((acc, val) => acc + val, 0);

    this.etoSummary = {
      total: normalizedDaily.length
        ? Number(totalFromFiltered.toFixed(3))
        : null,
      source,
      method,
      dataQuality,
      diagnostics: (summary as any).diagnostics,
      daily: normalizedDaily
    };
    this.etoDailyRows = normalizedDaily;
    this.rebuildCombinedDailyRows();
  }

  private applyWeeklyBalance(response: WeeklyBalanceResponse | null): void {
    if (!response) {
      this.weeklySummary = null;
      this.dailyRows = [];
      this.etoSummary = null;
      this.etoDailyRows = [];
      this.etcWeeklyTotal = null;
      this.weeklyKc = [];
      this.combinedDailyRows = [];
      this.weeklyMissingData = false;
      this.etoError = '';
      this.etoNotImplemented = false;
      this.grossNeedMmWeekly = null;
      this.litersPerPlantWeekly = null;
      this.irrigationHoursPerPlantWeekly = null;
      this.irrigationQuality = null;
      this.irrigationDiagnostics = null;
      return;
    }

    const normalizeNumber = (value: any): number | null => {
      if (value === null || value === undefined) return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    const translateSource = (source: any): string | null => {
      const raw = String(source || '')
        .trim()
        .toLowerCase();
      if (!raw) return null;
      if (raw === 'open_meteo')
        return this.translate.instant('BALANCE.SOURCES.OPEN_METEO');
      if (raw === 'aemet')
        return this.translate.instant('BALANCE.SOURCES.AEMET');
      if (raw === 'siar') return this.translate.instant('BALANCE.SOURCES.SIAR');
      if (raw === 'pluviometer')
        return this.translate.instant('BALANCE.SOURCES.PLUVIOMETER');
      return source;
    };

    const filteredDaily = Array.isArray(response.daily)
      ? response.daily.filter(day => this.isWithinSelectedRange(day?.date))
      : [];

    const mappedDaily = filteredDaily.map(day => ({
      date: day?.date || null,
      precipTotal: normalizeNumber(day?.precipTotal),
      precipEffective: normalizeNumber(day?.precipEffective),
      et0: normalizeNumber(day?.et0),
      kc: normalizeNumber(day?.kc),
      etc: normalizeNumber(day?.etc),
      kcSourceUsed: day?.kcSourceUsed ?? response.sources?.kc ?? null,
      pas: normalizeNumber(day?.pas),
      monthFactor: normalizeNumber(day?.monthFactor),
      kcInput: normalizeNumber(day?.kcInput),
      iviaVariantIdUsed:
        (day as any)?.iviaVariantIdUsed ??
        (response as any)?.iviaVariantIdUsed ??
        null,
      iviaCropName:
        (day as any)?.iviaCropName ?? (response as any)?.iviaCropName ?? null
    }));

    const precipTotal = normalizeNumber(response.summary?.precipTotalWeekly);
    const precipEffective = normalizeNumber(
      response.summary?.precipEffectiveWeekly
    );
    const et0Weekly = normalizeNumber(response.summary?.et0Weekly);
    const etcWeekly = normalizeNumber(response.summary?.etcWeekly);
    const quality = response.quality
      ? String(response.quality).toLowerCase()
      : null;
    this.irrigationQuality = response.irrigationQuality
      ? String(response.irrigationQuality).toLowerCase()
      : null;
    this.weeklyError = '';
    this.weeklyPending = false;

    this.weeklySummary = {
      total: precipTotal,
      effective: precipEffective,
      source: translateSource(response.sources?.precipitation),
      daily: mappedDaily.map(d => ({
        date: d.date,
        total: d.precipTotal,
        effective: d.precipEffective,
        source: translateSource(response.sources?.precipitation)
      }))
    };
    this.dailyRows = this.weeklySummary.daily || [];

    const et0FromDaily = mappedDaily
      .map(d => d.et0)
      .filter(
        (v): v is number => v !== null && v !== undefined && Number.isFinite(v)
      )
      .reduce((acc, val) => acc + val, 0);

    this.etoSummary = {
      total:
        et0Weekly !== null && et0Weekly !== undefined
          ? et0Weekly
          : mappedDaily.length
          ? Number(et0FromDaily.toFixed(3))
          : null,
      source: translateSource(response.sources?.et0),
      method: (response as any)?.method || null,
      dataQuality: quality,
      diagnostics: response.diagnostics,
      daily: mappedDaily.map(d => ({ date: d.date, eto: d.et0 }))
    };
    this.etoDailyRows = this.etoSummary.daily || [];
    this.etcWeeklyTotal =
      etcWeekly !== null && etcWeekly !== undefined
        ? etcWeekly
        : mappedDaily.length
        ? mappedDaily
            .map(d => d.etc)
            .filter(
              (v): v is number =>
                v !== null && v !== undefined && Number.isFinite(v)
            )
            .reduce((acc, val) => acc + val, 0)
        : null;

    this.grossNeedMmWeekly = normalizeNumber(
      response.summary?.grossNeedMmWeekly
    );
    this.litersPerPlantWeekly = normalizeNumber(
      response.summary?.litersPerPlantWeekly
    );
    this.irrigationHoursPerPlantWeekly = normalizeNumber(
      response.summary?.irrigationHoursPerPlantWeekly
    );
    this.irrigationDiagnostics =
      (response as any)?.diagnostics?.irrigationCalc ?? null;

    this.weeklyMissingData = quality === 'missing';
    this.etoError = '';
    this.etoNotImplemented = false;

    this.weeklyKc = response.sources?.kc
      ? [
          {
            week: '',
            kc: null,
            kcSourceUsed: response.sources.kc,
            pas: null,
            monthFactor: null
          }
        ]
      : [];

    this.combinedDailyRows = mappedDaily.map(d => ({
      date: d.date,
      precip: d.precipTotal ?? null,
      eto: d.et0 ?? null,
      kc: d.kc ?? null,
      etc: d.etc ?? null,
      kcSource: d.kcSourceUsed ?? null,
      kcPas: d.pas ?? null,
      kcMonthFactor: d.monthFactor ?? null,
      kcIviaVariantIdUsed: d.iviaVariantIdUsed ?? null,
      kcIviaCropName: d.iviaCropName ?? null
    }));
  }

  private applyWeeklyReport(report: WeeklyReportResponse | null): void {
    this.weeklyReport = report;
    if (!report) {
      this.reportNarrative = [];
      this.operationalSummary = [];
      return;
    }
    this.reportNarrative = this.buildNarrative(report);
    this.operationalSummary = this.buildOperationalSummary(report);
  }

  private buildNarrative(
    report: WeeklyReportResponse
  ): Array<{ key: string; params?: any }> {
    const items: Array<{ key: string; params?: any; severity?: string }> = [];
    const insights = Array.isArray(report.insights) ? [...report.insights] : [];
    const basisLabel = this.translate.instant(
      `BALANCE.REPORT.BASIS_LABEL.${report?.features?.comparison?.basis ||
        'unknown'}`
    );
    const roundVal = (v: any, digits: number) => {
      const num = Number(v);
      return Number.isFinite(num) ? Number(num.toFixed(digits)) : null;
    };
    const order = (s?: string) =>
      s === 'critical' ? 0 : s === 'warning' ? 1 : 2;

    insights.forEach(insight => {
      const code = insight?.code || '';
      const evidence = insight?.evidence || {};
      let params: any = {};
      let key: string | null = null;
      const requireParams = (...fields: string[]) =>
        fields.every(
          f =>
            params?.[f] !== null &&
            params?.[f] !== undefined &&
            !Number.isNaN(params?.[f])
        );
      if (code === 'DRAINAGE_DETECTED') {
        params = {
          episodesCount:
            evidence.drainageEventsCount ?? evidence.events ?? null,
          durationHours: roundVal(
            evidence.drainageTotalDurationHours ?? evidence.durationHours,
            1
          )
        };
        if (requireParams('episodesCount', 'durationHours')) {
          key = 'BALANCE.REPORT.NARRATIVE.DRAINAGE_DETECTED';
        } else {
          key = 'BALANCE.REPORT.NARRATIVE.DRAINAGE_DETECTED_SHORT';
          params = {};
        }
      } else if (code === 'IRRIGATION_EXCESS_VS_THEORETICAL') {
        params = {
          ratio:
            roundVal(evidence.ratio, 2) ??
            roundVal(report?.features?.comparison?.ratio, 2),
          diffPct:
            roundVal(evidence.diffPct, 0) ??
            roundVal(report?.features?.comparison?.diffPct, 0),
          basisLabel
        };
        if (params.ratio !== null || params.diffPct !== null) {
          key = 'BALANCE.REPORT.NARRATIVE.IRRIGATION_EXCESS_VS_THEORETICAL';
        }
      } else if (code === 'IRRIGATION_DEFICIT_VS_THEORETICAL') {
        params = {
          ratio:
            roundVal(evidence.ratio, 2) ??
            roundVal(report?.features?.comparison?.ratio, 2),
          diffPct:
            roundVal(evidence.diffPct, 0) ??
            roundVal(report?.features?.comparison?.diffPct, 0),
          basisLabel
        };
        if (params.ratio !== null || params.diffPct !== null) {
          key = 'BALANCE.REPORT.NARRATIVE.IRRIGATION_DEFICIT_VS_THEORETICAL';
        }
      } else if (code?.startsWith('DATA_QUALITY')) {
        const sourceMap: any = {
          DATA_QUALITY_FLOW_PARTIAL: 'riego',
          DATA_QUALITY_SOIL_MISSING: 'suelo',
          DATA_QUALITY_PROFILE_MISSING: 'perfil'
        };
        params = {
          sourceLabel:
            sourceMap[code] ||
            this.translate.instant('BALANCE.REPORT.LABEL.DATA_GENERIC')
        };
        key = 'BALANCE.REPORT.NARRATIVE.DATA_QUALITY';
      }
      if (key) {
        items.push({ key, params, severity: insight?.severity });
      }
    });

    if (report.quality && report.quality !== 'ok') {
      items.push({
        key: 'BALANCE.REPORT.NARRATIVE.DATA_QUALITY',
        params: { sourceLabel: report.quality },
        severity: 'info'
      });
    }

    items.sort((a, b) => order(a.severity) - order(b.severity));
    const safeLines: Array<{ key: string; params?: any }> = [];
    items.slice(0, 7).forEach(item => {
      const translated = this.translate.instant(item.key, item.params || {});
      if (!translated || translated === item.key) {
        return;
      }
      if (translated.includes('{') || translated.includes('}')) {
        return;
      }
      safeLines.push({ key: item.key, params: item.params });
    });
    return safeLines;
  }

  private buildOperationalSummary(
    report: WeeklyReportResponse
  ): Array<{ key: string; params?: any }> {
    const items: Array<{ key: string; params?: any; severity?: string }> = [];
    const translateSafe = (key: string, params?: any) => {
      const t = this.translate.instant(key, params || {});
      if (!t || t === key || t.includes('{')) return null;
      return { key, params };
    };
    const controlAvg = Number(report?.features?.soil?.controlAvg);
    const controlMin = Number(report?.features?.soil?.controlMin);
    const controlMax = Number(report?.features?.soil?.controlMax);
    const hasSoil =
      Number.isFinite(controlAvg) ||
      Number.isFinite(controlMin) ||
      Number.isFinite(controlMax);
    if (hasSoil) {
      const invalidRange =
        (Number.isFinite(controlMin) &&
          Number.isFinite(controlMax) &&
          controlMin > controlMax) ||
        (Number.isFinite(controlMin) && controlMin < 0) ||
        (Number.isFinite(controlMax) && controlMax < 0);
      if (invalidRange) {
        const res = translateSafe('BALANCE.REPORT.OPERATIONS.SOIL_INVALID');
        if (res) items.push(res);
      } else if (Number.isFinite(controlAvg)) {
        let soilKey: string | null = null;
        if (controlAvg > 110) soilKey = 'BALANCE.REPORT.OPERATIONS.SOIL_HIGH';
        else if (controlAvg >= 80)
          soilKey = 'BALANCE.REPORT.OPERATIONS.SOIL_OPTIMAL';
        else if (controlAvg >= 60)
          soilKey = 'BALANCE.REPORT.OPERATIONS.SOIL_MEDIUM';
        else soilKey = 'BALANCE.REPORT.OPERATIONS.SOIL_LOW';
        if (soilKey) {
          const res = translateSafe(soilKey, { controlAvg });
          if (res) items.push(res);
        }
      }
    }

    const drainage = report?.features?.drainage;
    let drainageEvents = drainage?.episodesCount ?? null;
    let drainageHours = drainage?.durationHours ?? null;
    if (!drainageEvents || !drainageHours) {
      const drainInsight = (report?.insights || []).find(
        i => i.code === 'DRAINAGE_DETECTED'
      );
      drainageEvents =
        drainageEvents ??
        drainInsight?.evidence?.drainageEventsCount ??
        drainInsight?.evidence?.events ??
        null;
      drainageHours =
        drainageHours ??
        drainInsight?.evidence?.drainageTotalDurationHours ??
        drainInsight?.evidence?.durationHours ??
        null;
    }
    if (drainageEvents !== null && drainageHours !== null) {
      const key =
        drainageHours > 24
          ? 'BALANCE.REPORT.OPERATIONS.DRAINAGE_PROLONGED'
          : 'BALANCE.REPORT.OPERATIONS.DRAINAGE_FULL';
      const res = translateSafe(key, {
        episodesCount: drainageEvents,
        durationHours:
          this.formatNumber(drainageHours, '1.0-1') ?? drainageHours
      });
      if (res) items.push(res);
    } else if (drainageEvents !== null || drainageHours !== null) {
      const res = translateSafe('BALANCE.REPORT.OPERATIONS.DRAINAGE_SHORT');
      if (res) items.push(res);
    }

    const comparison = report?.features?.comparison;
    const ratio = Number(comparison?.ratio);
    const diff = Number(comparison?.diffPct);
    const basisLabel = this.safeTranslate(
      `BALANCE.REPORT.BASIS_LABEL.${comparison?.basis || 'unknown'}`
    );
    if (Number.isFinite(ratio)) {
      let key: string | null = null;
      if (ratio > 10 || diff > 500) {
        key = 'BALANCE.REPORT.OPERATIONS.RATIO_EXTREME';
      } else if (ratio >= 1.2) key = 'BALANCE.REPORT.OPERATIONS.RATIO_ABOVE';
      else if (ratio <= 0.8) key = 'BALANCE.REPORT.OPERATIONS.RATIO_BELOW';
      else key = 'BALANCE.REPORT.OPERATIONS.RATIO_ALIGNED';
      const res = translateSafe(key, {
        ratio: this.formatNumber(ratio, '1.0-2') ?? ratio,
        basisLabel: basisLabel || ''
      });
      if (res) items.push(res);
    }

    const alertInsight = (report?.insights || []).some(
      i => i.severity === 'critical' || i.severity === 'warning'
    );
    if (alertInsight) {
      const res = translateSafe('BALANCE.REPORT.OPERATIONS.ALERTS_PRESENT');
      if (res) items.push(res);
    }

    if (report?.quality && report.quality !== 'ok') {
      const res = translateSafe('BALANCE.REPORT.OPERATIONS.DATA_QUALITY', {
        sourceList: report.quality
      });
      if (res) items.push(res);
    }

    return items.slice(0, 6);
  }

  public formatComparisonBasis(basis?: string | null): string {
    const key = `BALANCE.REPORT.BASIS_LABEL.${basis || 'unknown'}`;
    const translated = this.safeTranslate(key);
    if (!translated || translated === key) {
      return this.translate.instant('BALANCE.REPORT.BASIS_LABEL.unknown');
    }
    return translated;
  }

  public formatComparisonRatio(value: any): string {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    if (num > 10) {
      return this.safeTranslate('BALANCE.REPORT.LABEL.RATIO_EXTREME') || '—';
    }
    return this.formatNumber(num, '1.0-2') ?? '—';
  }

  public formatComparisonDiff(value: any): string | null {
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    if (num > 500) {
      return this.safeTranslate('BALANCE.REPORT.LABEL.DIFF_EXTREME') || null;
    }
    return `${this.formatPercent(num, '1.0-1') ?? '—'}%`;
  }

  public formatControlRange(min: any, max: any): string {
    const minNum = Number(min);
    const maxNum = Number(max);
    if (
      !Number.isFinite(minNum) ||
      !Number.isFinite(maxNum) ||
      minNum > maxNum ||
      minNum < 0 ||
      maxNum < 0
    ) {
      return this.safeTranslate('BALANCE.REPORT.LABEL.CONTROL_INVALID') || '—';
    }
    return `${minNum} – ${maxNum}`;
  }

  private formatNumber(value: any, digits: string = '1.0-2'): string | null {
    if (value === null || value === undefined) return null;
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    return this.decimalPipe.transform(num, digits);
  }

  private formatPercent(value: any, digits: string = '1.0-1'): string | null {
    return this.formatNumber(value, digits);
  }

  public formatConfidence(value: any): string {
    const num = Number(value);
    if (!Number.isFinite(num))
      return this.translate.instant('BALANCE.REPORT.CONFIDENCE_LABEL.unknown');
    if (num >= 0.66)
      return this.translate.instant('BALANCE.REPORT.CONFIDENCE_LABEL.high');
    if (num >= 0.33)
      return this.translate.instant('BALANCE.REPORT.CONFIDENCE_LABEL.medium');
    return this.translate.instant('BALANCE.REPORT.CONFIDENCE_LABEL.low');
  }

  public getInsightTitle(insight: WeeklyReportInsight): string {
    if (insight?.summaryKey) {
      const translated = this.safeTranslate(insight.summaryKey);
      if (translated) return translated;
    }
    const map: Record<string, string> = {
      DRAINAGE_DETECTED: 'BALANCE.REPORT.INSIGHT.DRAINAGE_DETECTED',
      IRRIGATION_EXCESS_VS_THEORETICAL:
        'BALANCE.REPORT.INSIGHT.IRRIGATION_EXCESS_VS_THEORETICAL',
      IRRIGATION_DEFICIT_VS_THEORETICAL:
        'BALANCE.REPORT.INSIGHT.IRRIGATION_DEFICIT_VS_THEORETICAL',
      DATA_QUALITY_FLOW_PARTIAL:
        'BALANCE.REPORT.INSIGHT.DATA_QUALITY_FLOW_PARTIAL',
      DATA_QUALITY_SOIL_MISSING:
        'BALANCE.REPORT.INSIGHT.DATA_QUALITY_SOIL_MISSING',
      DATA_QUALITY_PROFILE_MISSING:
        'BALANCE.REPORT.INSIGHT.DATA_QUALITY_PROFILE_MISSING',
      SOIL_STRESS_EPISODE: 'BALANCE.REPORT.INSIGHT.SOIL_STRESS_EPISODE'
    };
    const mapped = map[insight?.code || ''];
    if (mapped) {
      const translated = this.safeTranslate(mapped);
      if (translated) return translated;
    }
    return (
      this.safeTranslate('BALANCE.REPORT.INSIGHT.FALLBACK_TITLE') ||
      'Hallazgo del sistema'
    );
  }

  public getInsightSubtitle(insight: WeeklyReportInsight): string {
    const evidence = insight?.evidence || {};
    const fmtNum = (v: any, d: string = '1.0-2') =>
      this.formatNumber(v, d) ?? '—';
    switch (insight?.code) {
      case 'DRAINAGE_DETECTED':
        if (
          evidence.drainageEventsCount != null &&
          evidence.drainageTotalDurationHours != null
        ) {
          return `${evidence.drainageEventsCount} ${this.translate.instant(
            'BALANCE.REPORT.LABEL.EVENTS'
          )} · ${fmtNum(evidence.drainageTotalDurationHours, '1.0-1')} h`;
        }
        return (
          this.safeTranslate(
            'BALANCE.REPORT.NARRATIVE.DRAINAGE_DETECTED_SHORT'
          ) || ''
        );
      case 'IRRIGATION_EXCESS_VS_THEORETICAL':
      case 'IRRIGATION_DEFICIT_VS_THEORETICAL':
        if (evidence.ratio != null || evidence.diffPct != null) {
          const ratio = fmtNum(evidence.ratio);
          const diff =
            evidence.diffPct != null
              ? `${fmtNum(evidence.diffPct, '1.0-1')}%`
              : '';
          return diff ? `${ratio}× · ${diff}` : `${ratio}×`;
        }
        return insight.code === 'IRRIGATION_EXCESS_VS_THEORETICAL'
          ? this.safeTranslate(
              'BALANCE.REPORT.NARRATIVE.IRRIGATION_EXCESS_VS_THEORETICAL'
            ) || ''
          : this.safeTranslate(
              'BALANCE.REPORT.NARRATIVE.IRRIGATION_DEFICIT_VS_THEORETICAL'
            ) || '';
      case 'DATA_QUALITY_FLOW_PARTIAL':
        return (
          this.safeTranslate('BALANCE.REPORT.LABEL.DATA_IRRIGATION_PARTIAL') ||
          ''
        );
      case 'DATA_QUALITY_SOIL_MISSING':
        return (
          this.safeTranslate('BALANCE.REPORT.LABEL.DATA_SOIL_MISSING') || ''
        );
      case 'DATA_QUALITY_PROFILE_MISSING':
        return (
          this.safeTranslate('BALANCE.REPORT.LABEL.DATA_PROFILE_MISSING') || ''
        );
      default:
        return (
          this.safeTranslate('BALANCE.REPORT.INSIGHT.FALLBACK_SUBTITLE') || ''
        );
    }
  }

  private safeTranslate(key: string | null | undefined): string | null {
    if (!key) return null;
    const translated = this.translate.instant(key);
    if (translated && translated !== key && !translated.includes('{')) {
      return translated;
    }
    return null;
  }

  public getInsightDetails(
    insight: WeeklyReportInsight
  ): Array<{ label: string; value: string | null }> {
    const evidence = insight?.evidence || {};
    const rows: Array<{ label: string; value: string | null }> = [];
    const pushRow = (
      labelKey: string,
      value: any,
      fmt: (v: any) => string | null = v => this.formatNumber(v)
    ) => {
      const label = this.safeTranslate(labelKey);
      if (!label) return;
      const val = fmt(value);
      if (val === null) return;
      rows.push({ label, value: val });
    };
    if (insight?.code === 'DRAINAGE_DETECTED') {
      pushRow(
        'BALANCE.REPORT.EVIDENCE.EPISODES',
        evidence.drainageEventsCount ?? evidence.events,
        v => v?.toString?.() || null
      );
      pushRow(
        'BALANCE.REPORT.EVIDENCE.DURATION_HOURS',
        evidence.drainageTotalDurationHours ?? evidence.durationHours,
        v => this.formatNumber(v, '1.0-1')
      );
    }
    if (
      insight?.code === 'IRRIGATION_EXCESS_VS_THEORETICAL' ||
      insight?.code === 'IRRIGATION_DEFICIT_VS_THEORETICAL'
    ) {
      pushRow('BALANCE.REPORT.EVIDENCE.RATIO', evidence.ratio, v =>
        this.formatNumber(v, '1.0-2')
      );
      pushRow('BALANCE.REPORT.EVIDENCE.DIFFPCT', evidence.diffPct, v =>
        this.formatNumber(v, '1.0-1')
      );
      const basisLabel =
        evidence.basisLabel ||
        this.safeTranslate(
          `BALANCE.REPORT.BASIS_LABEL.${evidence.basis || 'unknown'}`
        );
      pushRow(
        'BALANCE.REPORT.EVIDENCE.BASIS',
        basisLabel,
        v => v?.toString?.() || null
      );
    }
    if (insight?.code === 'SOIL_STRESS_EPISODE') {
      pushRow(
        'BALANCE.REPORT.EVIDENCE.EPISODES',
        evidence.events,
        v => v?.toString?.() || null
      );
    }
    return rows;
  }

  private mapCrops(
    crops: any[]
  ): Array<{
    id: number;
    name: string;
    code?: string;
    category?: string;
    displayName?: string;
    iviaVariants?: Array<{ id: number; name: string }>;
    iviaDefaultVariantId?: number | null;
  }> {
    return (crops || []).map(crop => ({
      ...crop,
      displayName: this.formatCropLabel(crop),
      iviaVariants: Array.isArray(crop?.iviaVariants) ? crop.iviaVariants : [],
      iviaDefaultVariantId: crop?.iviaDefaultVariantId ?? null
    }));
  }

  private hasPluviometerSensor(sensors: any[]): boolean {
    return (sensors || []).some(
      sensor => (sensor?.sensorType || sensor?.type) === 'PL'
    );
  }

  private updatePrecipStationValidator(source: PrecipitationSource): void {
    const aemetCtrl = this.form.get('aemetStationId');
    const siarCtrl = this.form.get('siarStationId');
    if (aemetCtrl) {
      if (source === 'aemet') {
        aemetCtrl.setValidators([Validators.required]);
      } else {
        aemetCtrl.clearValidators();
      }
      aemetCtrl.updateValueAndValidity({ emitEvent: false });
    }
    if (siarCtrl) {
      if (source === 'siar') {
        siarCtrl.setValidators([Validators.required]);
      } else {
        siarCtrl.clearValidators();
      }
      siarCtrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  private updateEtoStationValidator(source: EtoSource): void {
    const etoAemetCtrl = this.form.get('etoAemetStationId');
    const etoSiarCtrl = this.form.get('etoSiarStationId');
    if (etoAemetCtrl) {
      if (source === 'aemet') {
        etoAemetCtrl.setValidators([Validators.required]);
      } else {
        etoAemetCtrl.clearValidators();
      }
      etoAemetCtrl.updateValueAndValidity({ emitEvent: false });
    }
    if (etoSiarCtrl) {
      if (source === 'siar') {
        etoSiarCtrl.setValidators([Validators.required]);
      } else {
        etoSiarCtrl.clearValidators();
      }
      etoSiarCtrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  public onEtoDebugToggle(): void {
    this.refreshWeekly();
  }

  public openWeeklyReport(): void {
    if (!this.weeklyReportDialog) return;
    this.weeklyReportDialogRef = this.dialog.open(this.weeklyReportDialog, {
      width: '90vw',
      maxWidth: '1100px',
      autoFocus: false,
      restoreFocus: true,
      panelClass: 'report-dialog-panel'
    });
  }

  public closeWeeklyReport(): void {
    this.weeklyReportDialogRef?.close();
    this.weeklyReportDialogRef = undefined;
  }

  public canOpenChatbot(): boolean {
    return this.isAdmin() && !!Number(this.form.get('moduleId')?.value);
  }

  public canSendChatMessage(): boolean {
    return (
      this.canOpenChatbot() &&
      !this.chatSending &&
      this.chatInput.trim().length > 0
    );
  }

  public getSelectedModuleName(): string {
    const moduleId = Number(this.form.get('moduleId')?.value);
    if (!moduleId) {
      return '-';
    }
    const selected = (this.modules || []).find(
      mod => Number(mod?.idModule) === moduleId
    );
    return String(selected?.name || selected?.moduleName || moduleId);
  }

  public openChatbot(): void {
    if (!this.chatbotDialog || !this.canOpenChatbot()) return;
    this.chatError = '';
    this.restoreChatState(this.getSelectedModuleId());
    this.closeChatbot();
    this.chatbotDialogRef = this.dialog.open(this.chatbotDialog, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      autoFocus: false,
      restoreFocus: true,
      panelClass: 'chatbot-dialog-panel'
    });
    this.chatbotDialogRef
      .afterOpened()
      .pipe(take(1))
      .subscribe(() => this.scrollChatToBottom());
  }

  public closeChatbot(): void {
    this.chatbotDialogRef?.close();
    this.chatbotDialogRef = undefined;
  }

  public onChatInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendChatMessage();
    }
  }

  public sendChatMessage(): void {
    if (!this.canSendChatMessage()) {
      return;
    }

    const moduleId = this.getSelectedModuleId();
    if (!moduleId) {
      this.chatError = this.translate.instant('BALANCE.CHATBOT_NO_CONTEXT');
      return;
    }

    const text = this.chatInput.trim();
    const userMessage = this.createChatMessage('user', text);
    this.chatMessages = [...this.chatMessages, userMessage];
    this.chatInput = '';
    this.chatError = '';
    this.chatSending = true;
    this.persistChatState(moduleId);
    this.scrollChatToBottom();

    this.moduleAgentChatService
      .postModuleAgentChat(moduleId, text, {
        tz: this.getChatTimezone(),
        locale: this.getChatLocale(),
        wantsCharts: true
      })
      .pipe(
        take(1),
        finalize(() => {
          this.chatSending = false;
          this.persistChatState(moduleId);
          this.scrollChatToBottom();
        })
      )
      .subscribe({
        next: response => this.handleChatResponse(moduleId, response),
        error: err => {
          this.chatError = this.resolveChatError(err);
          const assistantError = this.createChatMessage(
            'assistant',
            this.chatError,
            undefined,
            { isError: true }
          );
          this.chatMessages = [...this.chatMessages, assistantError];
          this.persistChatState(moduleId);
          this.scrollChatToBottom();
          this.snackBar.open(
            this.chatError,
            this.translate.instant('COMMON.CLOSE'),
            {
              duration: 4500
            }
          );
        }
      });
  }

  public getChatStatusLabel(): string {
    if (this.chatMeta?.simulated) {
      return this.translate.instant('BALANCE.CHATBOT_STATUS_SIMULATED');
    }
    return this.translate.instant('BALANCE.CHATBOT_STATUS_CONNECTED');
  }

  public trackByChatMessage(_index: number, msg: ChatMessage): string {
    return msg.id;
  }

  public trackByChatBlock(
    index: number,
    block: ModuleAgentChatRenderBlock
  ): string {
    if (!block) {
      return `block-${index}`;
    }
    if (block.type === 'text') {
      return `text-${index}-${block.text?.length || 0}`;
    }
    return `chart-${index}-${block.chart?.id || 'chart'}`;
  }

  public hasRenderableBlocks(msg: ChatMessage): boolean {
    return (
      !!msg &&
      msg.role === 'assistant' &&
      Array.isArray(msg.renderBlocks) &&
      msg.renderBlocks.length > 0
    );
  }

  public isTextRenderBlock(
    block: ModuleAgentChatRenderBlock
  ): block is ModuleAgentChatTextBlock {
    return !!block && block.type === 'text';
  }

  public isChartRenderBlock(
    block: ModuleAgentChatRenderBlock
  ): block is ModuleAgentChatChartBlock {
    return !!block && block.type === 'chart';
  }

  public renderChatMarkdown(msg: ChatMessage): string {
    if (!msg) return '';
    const cacheKey = msg.id || `tmp-${msg.role}-${msg.createdAt || ''}`;
    return this.renderMarkdownText(msg.text, cacheKey);
  }

  public renderChatBlockMarkdown(
    msg: ChatMessage,
    block: ModuleAgentChatTextBlock,
    index: number
  ): string {
    if (!msg || !block) {
      return '';
    }
    return this.renderMarkdownText(block.text, `${msg.id}-block-${index}`);
  }

  public onChatChartRendered(): void {
    this.deferChatAutoscroll();
  }

  private renderMarkdownText(text: string, cacheKey: string): string {
    if (!cacheKey) {
      return '';
    }

    const cached = this.chatMarkdownCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const markdown = String(text || '');
    const rendered = marked.parse(markdown, {
      gfm: true,
      breaks: true
    }) as string;
    const sanitized = DOMPurify.sanitize(rendered, {
      USE_PROFILES: { html: true }
    });
    this.chatMarkdownCache.set(cacheKey, sanitized);
    return sanitized;
  }

  private getChatTimezone(): string {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return timezone || 'UTC';
    } catch (_) {
      return 'UTC';
    }
  }

  private getChatLocale(): string {
    const rawLocale = String(
      this.translate.currentLang ||
        this.translate.getDefaultLang() ||
        navigator?.language ||
        'es-ES'
    ).trim();
    if (!rawLocale) {
      return 'es-ES';
    }
    if (rawLocale.toLowerCase() === 'es') return 'es-ES';
    if (rawLocale.toLowerCase() === 'en') return 'en-US';
    if (rawLocale.toLowerCase() === 'fr') return 'fr-FR';
    return rawLocale;
  }

  public getSelectedModuleId(): number | null {
    const moduleId = Number(this.form.get('moduleId')?.value);
    return moduleId > 0 ? moduleId : null;
  }

  private createChatMessage(
    role: 'user' | 'assistant',
    text: string,
    createdAt?: string | null,
    options?: {
      renderBlocks?: ModuleAgentChatRenderBlock[];
      isError?: boolean;
    }
  ): ChatMessage {
    this.chatMessageSequence += 1;
    return {
      id: `${role}-${Date.now()}-${this.chatMessageSequence}`,
      role,
      text: String(text || ''),
      createdAt:
        createdAt && !Number.isNaN(Date.parse(createdAt))
          ? createdAt
          : new Date().toISOString(),
      renderBlocks: this.cloneRenderBlocks(options?.renderBlocks),
      isError: !!options?.isError
    };
  }

  private handleChatResponse(
    moduleId: number,
    response: ModuleAgentChatResponse
  ): void {
    const meta = response?.meta || {};
    this.chatMeta = {
      simulated: !!meta?.simulated,
      provider: meta?.provider ? String(meta.provider) : null,
      hasCharts: !!meta?.hasCharts,
      chartsCount: Number(meta?.chartsCount || 0)
    };

    const conversation = Array.isArray(response?.conversation)
      ? response.conversation
      : [];
    const assistantTurn = this.getAssistantTurn(conversation);
    const normalizedBlocks = this.normalizeRenderBlocks(response?.renderBlocks);
    const fallbackText = this.resolveAssistantText(response, assistantTurn);
    const hasRenderableBlocks = normalizedBlocks.length > 0;

    if (!hasRenderableBlocks && !fallbackText) {
      this.persistChatState(moduleId);
      return;
    }

    const assistantMessage = this.createChatMessage(
      'assistant',
      fallbackText,
      assistantTurn?.createdAt || undefined,
      {
        renderBlocks: hasRenderableBlocks ? normalizedBlocks : undefined
      }
    );
    this.chatMessages = [...this.chatMessages, assistantMessage];
    this.persistChatState(moduleId);
    this.deferChatAutoscroll();
  }

  private getAssistantTurn(
    conversation: ModuleAgentChatTurn[]
  ): ModuleAgentChatTurn | null {
    if (!Array.isArray(conversation) || !conversation.length) {
      return null;
    }

    return (
      [...conversation].reverse().find(turn => {
        return String(turn?.role || '').toLowerCase() === 'assistant';
      }) || null
    );
  }

  private resolveAssistantText(
    response: ModuleAgentChatResponse,
    assistantTurn: ModuleAgentChatTurn | null
  ): string {
    const replyText = String(response?.replyText || '').trim();
    if (replyText) {
      return replyText;
    }

    const turnText = String(assistantTurn?.text || '').trim();
    if (turnText) {
      return turnText;
    }

    const normalizedBlocks = this.normalizeRenderBlocks(response?.renderBlocks);
    const textFromBlocks = normalizedBlocks
      .filter(
        (block): block is ModuleAgentChatTextBlock =>
          this.isTextRenderBlock(block)
      )
      .map(block => String(block.text || '').trim())
      .filter(Boolean)
      .join('\n\n')
      .trim();
    return textFromBlocks;
  }

  private normalizeRenderBlocks(input: any): ModuleAgentChatRenderBlock[] {
    if (!Array.isArray(input)) {
      return [];
    }

    const normalized: ModuleAgentChatRenderBlock[] = [];
    for (const rawBlock of input) {
      if (!rawBlock || typeof rawBlock !== 'object') {
        continue;
      }

      if (String(rawBlock.type || '').toLowerCase() === 'text') {
        const text = String(rawBlock.text || '');
        if (text.trim().length > 0) {
          normalized.push({
            type: 'text',
            text
          });
        }
        continue;
      }

      if (String(rawBlock.type || '').toLowerCase() === 'chart') {
        const chart = this.normalizeChartMeta(rawBlock.chart);
        const chartData = this.normalizeChartData(rawBlock.chartData);
        if (!chart || !chartData) {
          continue;
        }
        normalized.push({
          type: 'chart',
          chart,
          chartData
        });
      }
    }

    return normalized;
  }

  private normalizeChartMeta(input: any): ModuleAgentChatChartMeta | null {
    if (!input || typeof input !== 'object') {
      return null;
    }

    const id = String(input.id || '').trim();
    const chartKind = String(input.chartKind || '').trim();
    const metric = String(input.metric || '').trim();
    const range = this.normalizeRange(input.range);

    if (!id || !chartKind || !metric || !range) {
      return null;
    }

    return {
      id,
      chartKind,
      metric,
      range,
      title: input.title ? String(input.title) : null,
      note: input.note ? String(input.note) : null,
      sensors: Array.isArray(input.sensors)
        ? input.sensors.map((sensor: any) => String(sensor))
        : undefined
    };
  }

  private normalizeChartData(input: any): ModuleAgentChatChartData | null {
    if (!input || typeof input !== 'object') {
      return null;
    }

    const source = String(input.source || '').trim();
    const metric = String(input.metric || '').trim();
    const range = this.normalizeRange(input.range);
    const rawSeries = Array.isArray(input.series) ? input.series : [];

    if (!source || !metric || !range) {
      return null;
    }

    const series = rawSeries
      .map((rawItem: any) => {
        const name = String(rawItem?.name || '').trim();
        const data = Array.isArray(rawItem?.data)
          ? rawItem.data
              .map((point: any) => this.normalizeSeriesPoint(point))
              .filter((point): point is [number, number | null] => !!point)
          : [];
        if (!name) {
          return null;
        }
        return {
          id: rawItem?.id ? String(rawItem.id) : undefined,
          name,
          data,
          type:
            rawItem?.type === 'spline' || rawItem?.type === 'column'
              ? rawItem.type
              : rawItem?.type === 'line'
                ? 'line'
                : undefined,
          color: rawItem?.color ? String(rawItem.color) : undefined,
          zoneAxis:
            rawItem?.zoneAxis === 'x' ||
            rawItem?.zoneAxis === 'y' ||
            rawItem?.zoneAxis === 'z'
              ? rawItem.zoneAxis
              : undefined,
          zones: this.normalizeChartZones(rawItem?.zones),
          showInLegend:
            typeof rawItem?.showInLegend === 'boolean'
              ? rawItem.showInLegend
              : undefined,
          linkedTo: rawItem?.linkedTo ? String(rawItem.linkedTo) : null
        };
      })
      .filter(Boolean) as ModuleAgentChatChartData['series'];

    return {
      source,
      sourceSeriesKey: input.sourceSeriesKey
        ? String(input.sourceSeriesKey)
        : null,
      metric,
      visualization: input.visualization ? String(input.visualization) : null,
      range,
      series
    };
  }

  private normalizeChartZones(
    input: any
  ): Array<{ value?: number; color?: string }> | undefined {
    if (!Array.isArray(input)) {
      return undefined;
    }

    const zones = input
      .map((rawZone: any) => {
        if (!rawZone || typeof rawZone !== 'object') {
          return null;
        }

        const value =
          rawZone.value === undefined || rawZone.value === null
            ? undefined
            : Number(rawZone.value);
        const color = rawZone.color ? String(rawZone.color) : undefined;
        if (value !== undefined && !Number.isFinite(value)) {
          return null;
        }
        if (value === undefined && !color) {
          return null;
        }

        return {
          value,
          color
        };
      })
      .filter(Boolean) as Array<{ value?: number; color?: string }>;

    return zones.length ? zones : undefined;
  }

  private normalizeRange(input: any): { start: string; end: string } | null {
    if (!input || typeof input !== 'object') {
      return null;
    }

    const start = String(input.start || '').trim();
    const end = String(input.end || '').trim();
    if (!start || !end) {
      return null;
    }

    return { start, end };
  }

  private normalizeSeriesPoint(point: any): [number, number | null] | null {
    if (!Array.isArray(point) || point.length < 2) {
      return null;
    }

    const x = Number(point[0]);
    if (!Number.isFinite(x)) {
      return null;
    }

    const rawY = point[1];
    if (rawY === null || rawY === undefined || rawY === '') {
      return [x, null];
    }

    const y = Number(rawY);
    if (!Number.isFinite(y)) {
      return null;
    }

    return [x, y];
  }

  private cloneRenderBlocks(
    blocks?: ModuleAgentChatRenderBlock[]
  ): ModuleAgentChatRenderBlock[] | undefined {
    if (!Array.isArray(blocks) || !blocks.length) {
      return undefined;
    }

    return blocks.map(block => {
      if (block.type === 'text') {
        return {
          type: 'text',
          text: block.text
        };
      }

      return {
        type: 'chart',
        chart: {
          ...block.chart,
          range: { ...block.chart.range },
          sensors: Array.isArray(block.chart.sensors)
            ? [...block.chart.sensors]
            : undefined
        },
        chartData: {
          ...block.chartData,
          range: { ...block.chartData.range },
          series: (block.chartData.series || []).map(series => ({
            ...series,
            zones: Array.isArray(series.zones)
              ? series.zones.map(zone => ({ ...zone }))
              : undefined,
            data: (series.data || []).map(point => [point[0], point[1]])
          }))
        }
      };
    });
  }

  private resolveChatError(err: any): string {
    const payload = err?.error;
    const first = Array.isArray(payload) ? payload[0] : payload;
    const code = String(first?.code || payload?.code || '').toUpperCase();

    switch (code) {
      case 'MODULE_NOT_FOUND':
        return this.translate.instant('BALANCE.CHATBOT_ERROR_MODULE_NOT_FOUND');
      case 'CLIENT_NOT_ALLOWED':
        return this.translate.instant('BALANCE.CHATBOT_ERROR_CLIENT_NOT_ALLOWED');
      case 'TEXT_REQUIRED':
        return this.translate.instant('BALANCE.CHATBOT_ERROR_TEXT_REQUIRED');
      case 'TEXT_TOO_LONG':
        return this.translate.instant('BALANCE.CHATBOT_ERROR_TEXT_TOO_LONG');
      default:
        return this.translate.instant('BALANCE.CHATBOT_ERROR_GENERIC');
    }
  }

  private restoreChatState(moduleId: number | null): void {
    if (!moduleId) {
      this.chatMessages = [];
      this.chatMeta = null;
      this.chatError = '';
      this.chatInput = '';
      return;
    }
    this.chatMessages = [...(this.chatHistoryByModule.get(moduleId) || [])];
    this.chatMeta = this.chatMetaByModule.get(moduleId) || null;
    this.chatError = '';
    this.chatInput = '';
    this.scrollChatToBottom();
  }

  private persistChatState(moduleId: number): void {
    this.chatHistoryByModule.set(moduleId, [...this.chatMessages]);
    if (this.chatMeta) {
      this.chatMetaByModule.set(moduleId, { ...this.chatMeta });
    } else {
      this.chatMetaByModule.delete(moduleId);
    }
  }

  private scrollChatToBottom(): void {
    const container = this.chatbotScroll?.nativeElement;
    if (!container) return;
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  }

  private deferChatAutoscroll(): void {
    this.scrollChatToBottom();
    setTimeout(() => this.scrollChatToBottom(), 100);
    setTimeout(() => this.scrollChatToBottom(), 250);
  }

  public formatEtoQuality(value: any): string {
    const key = String(value || '')
      .trim()
      .toLowerCase();
    if (key === 'ok') return this.translate.instant('BALANCE.ETO_QUALITY_OK');
    if (key === 'partial')
      return this.translate.instant('BALANCE.ETO_QUALITY_PARTIAL');
    if (key === 'missing')
      return this.translate.instant('BALANCE.ETO_QUALITY_MISSING');
    return this.translate.instant('BALANCE.ETO_QUALITY_UNKNOWN');
  }

  public formatDiagnostics(
    diagnostics: any
  ): Array<{ key: string; value: string }> {
    if (!diagnostics || typeof diagnostics !== 'object') {
      return [];
    }
    return Object.entries(diagnostics).map(([key, value]) => ({
      key,
      value:
        value === null || value === undefined
          ? '-'
          : typeof value === 'string'
          ? value
          : JSON.stringify(value)
    }));
  }

  public hasCropSelected(): boolean {
    const value = this.form.get('cropId')?.value;
    return !(value === null || value === undefined || value === '');
  }

  public formatIrrigationHours(hours: number | null): string {
    if (hours === null || hours === undefined || Number.isNaN(Number(hours))) {
      return '—';
    }
    const totalMinutes = Math.round(Number(hours) * 60);
    const hh = Math.floor(totalMinutes / 60);
    const mm = totalMinutes % 60;
    return `${hh}:${mm.toString().padStart(2, '0')}`;
  }

  public getSelectedCrop(): any | null {
    const cropId = this.form.get('cropId')?.value;
    return this.crops.find(c => c.id === cropId) || null;
  }

  public hasIncompletePlantingParams(): boolean {
    if (!this.hasCropSelected()) return false;
    const dc = this.parsePlantingValue(this.form.get('dc')?.value);
    const df = this.parsePlantingValue(this.form.get('df')?.value);
    const dp = this.parsePlantingValue(this.form.get('dp')?.value);
    return dc === null || df === null || dp === null;
  }

  public formatCropLabel(crop: any): string {
    if (!crop) return '';
    const code = (crop.code || crop.name || '').toString().toUpperCase();
    if (!code) return crop.name || '';
    const key = `BALANCE.CROPS.${code}`;
    const translated = this.translate.instant(key);
    if (translated && translated !== key) {
      return translated;
    }
    return crop.name || code;
  }

  public formatVariantLabel(variant: any): string {
    if (!variant) return '';
    const raw = (variant.name || '').toString();
    const slug = raw
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toUpperCase();
    if (slug) {
      const key = `BALANCE.IVIA_VARIANTS.${slug}`;
      const translated = this.translate.instant(key);
      if (translated && translated !== key) {
        return translated;
      }
    }
    return raw;
  }

  private normalizePrecipitationSource(value: any): PrecipitationSource {
    const raw = String(value || '')
      .trim()
      .toLowerCase();
    if (raw === 'nasa' || raw === 'open_meteo') {
      return 'open_meteo';
    }
    if (raw === 'pluviometer' || raw === 'aemet' || raw === 'siar') {
      return raw as PrecipitationSource;
    }
    return 'open_meteo';
  }

  private optionalPositiveValidator(minValue: number = 0) {
    return (control: any) => {
      const value = control?.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const num = Number(value);
      if (!Number.isFinite(num) || num <= minValue) {
        return { positiveNumber: true };
      }
      return null;
    };
  }

  private optionalPercentValidator() {
    return (control: any) => {
      const value = control?.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const num = Number(value);
      if (!Number.isFinite(num) || num <= 0 || num > 100) {
        return { percentRange: true };
      }
      return null;
    };
  }

  private parsePlantingValue(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) && num > 0 ? num : null;
  }

  private normalizePercentFromBackend(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    if (num <= 1) {
      return Number((num * 100).toFixed(2));
    }
    return Number(num.toFixed(2));
  }

  private normalizePercentToSend(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    if (num <= 1) {
      return Number((num * 100).toFixed(2));
    }
    return Number(num.toFixed(2));
  }

  private isWithinSelectedRange(dateInput: any): boolean {
    if (!dateInput) return false;
    const date = new Date(dateInput);
    if (Number.isNaN(date.getTime())) return false;
    const startValue = this.rangeForm?.get('start')?.value;
    const endValue = this.rangeForm?.get('end')?.value;
    const start = startValue ? new Date(startValue) : null;
    const end = endValue ? new Date(endValue) : null;
    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);
    const time = date.getTime();
    if (start && time < start.getTime()) return false;
    if (end && time > end.getTime()) return false;
    return true;
  }

  private formatKcSource(value: any): string {
    const key = String(value || '')
      .trim()
      .toUpperCase();
    if (!key) return this.translate.instant('BALANCE.KC_SOURCE_NONE');
    if (key === 'IVIA') return 'IVIA';
    if (key === 'SIAR') return 'SIAR';
    return key;
  }

  private getIsoWeekKey(dateInput: any): string | null {
    if (!dateInput) return null;
    const date = new Date(dateInput);
    if (Number.isNaN(date.getTime())) return null;
    const target = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    const dayNum = target.getUTCDay() || 7;
    target.setUTCDate(target.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(
      ((target.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
    );
    const weekStr = weekNo < 10 ? `0${weekNo}` : `${weekNo}`;
    return `${target.getUTCFullYear()}-W${weekStr}`;
  }

  private normalizeKcEntries(kcData: any): WeeklyKc[] {
    const normalizeNumber = (value: any): number | null => {
      if (value === null || value === undefined) return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };
    const weeksRaw = Array.isArray(kcData?.weeks)
      ? kcData.weeks
      : Array.isArray(kcData)
      ? kcData
      : [];
    return weeksRaw
      .map((entry: any) => {
        let week = entry?.week || entry?.isoWeek || entry?.weekKey || null;
        if (
          !week &&
          entry?.weekOfYear !== undefined &&
          entry?.weekOfYear !== null
        ) {
          const weekNum = Number(entry?.weekOfYear);
          const yearRaw = entry?.year ?? entry?.weekYear ?? entry?.anio ?? null;
          const year =
            yearRaw !== null && yearRaw !== undefined ? String(yearRaw) : null;
          if (Number.isFinite(weekNum) && year) {
            const paddedWeek = weekNum < 10 ? `0${weekNum}` : String(weekNum);
            week = `${year}-W${paddedWeek}`;
          }
        }
        if (!week) return null;
        return {
          week: String(week),
          kc: normalizeNumber(entry?.kc ?? entry?.kcValue),
          kcSourceUsed:
            entry?.kcSourceUsed ?? entry?.source ?? entry?.kcSource ?? null,
          pas: normalizeNumber(entry?.pas),
          monthFactor: normalizeNumber(entry?.monthFactor ?? entry?.factor)
        } as WeeklyKc;
      })
      .filter(Boolean) as WeeklyKc[];
  }

  private applyWeeklyKc(kcData: any): void {
    this.weeklyKc = this.normalizeKcEntries(kcData);
    this.rebuildCombinedDailyRows();
  }

  private rebuildCombinedDailyRows(): void {
    const map = new Map<
      string,
      {
        date: string | null;
        precip: number | null;
        eto: number | null;
        kc: number | null;
        etc: number | null;
        kcSource?: string | null;
        kcPas?: number | null;
        kcMonthFactor?: number | null;
      }
    >();
    const add = (
      date: any,
      patch: {
        precip?: number | null;
        eto?: number | null;
        etc?: number | null;
      }
    ) => {
      if (!date) return;
      const key = String(date);
      const existing = map.get(key) || {
        date: key,
        precip: null,
        eto: null,
        kc: null,
        etc: null
      };
      if (patch.precip !== undefined) {
        existing.precip = patch.precip;
      }
      if (patch.eto !== undefined) {
        existing.eto = patch.eto;
      }
      if (patch.etc !== undefined) {
        existing.etc = patch.etc;
      }
      map.set(key, existing);
    };

    this.dailyRows.forEach(day => {
      add((day as any).date, { precip: (day as any).total ?? null });
    });
    this.etoDailyRows.forEach(day => {
      add((day as any).date, { eto: (day as any).eto ?? null });
    });

    const kcMap = new Map<string, WeeklyKc>();
    (this.weeklyKc || []).forEach(entry => {
      if (entry?.week) {
        kcMap.set(String(entry.week), entry);
      }
    });

    const rows = Array.from(map.values());
    rows.forEach(row => {
      const weekKey = this.getIsoWeekKey(row.date);
      if (!weekKey) return;
      const kcEntry = kcMap.get(weekKey);
      if (!kcEntry) return;
      row.kc = kcEntry.kc ?? null;
      row.kcSource = kcEntry.kcSourceUsed ?? null;
      row.kcPas = kcEntry.pas ?? null;
      row.kcMonthFactor = kcEntry.monthFactor ?? null;
    });

    rows.sort((a, b) => {
      const aTime = Date.parse(String(a.date || ''));
      const bTime = Date.parse(String(b.date || ''));
      if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
        return aTime - bTime;
      }
      return String(a.date || '').localeCompare(String(b.date || ''));
    });

    const startValue = this.rangeForm?.get('start')?.value;
    const endValue = this.rangeForm?.get('end')?.value;
    const startDate = startValue ? new Date(startValue) : null;
    const endDate = endValue ? new Date(endValue) : null;
    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(23, 59, 59, 999);

    const filtered = rows.filter(row => {
      if (!startDate && !endDate) {
        return true;
      }
      if (!row.date) return false;
      const time = Date.parse(String(row.date));
      if (Number.isNaN(time)) return false;
      if (startDate && time < startDate.getTime()) return false;
      if (endDate && time > endDate.getTime()) return false;
      return true;
    });

    this.combinedDailyRows = filtered;
  }

  public shouldShowSelectedStationPlaceholder(): boolean {
    const selectedId = this.form.get('aemetStationId')?.value;
    if (!selectedId || !this.selectedStationLabel) {
      return false;
    }
    return !this.stations.some(st => String(st.idema) === String(selectedId));
  }

  public formatStationLabel(station: AemetStation): string {
    if (!station) return '';
    const idema = station.idema ? String(station.idema) : '';
    const name = station.name ? String(station.name) : '';
    if (idema && name) {
      return `${idema} - ${name}`;
    }
    return idema || name;
  }

  public buildStationTooltip(station: AemetStation): string {
    if (!station) return '';
    const parts: string[] = [];
    if (station.municipality) {
      parts.push(
        `${this.translate.instant('BALANCE.AEMET_MUNICIPALITY')}: ${
          station.municipality
        }`
      );
    }
    if (station.altitude !== null && station.altitude !== undefined) {
      parts.push(
        `${this.translate.instant('BALANCE.AEMET_ALTITUDE')}: ${
          station.altitude
        } m`
      );
    }
    return parts.join(' | ');
  }

  private syncSelectedStationLabel(): void {
    const selectedId = this.form.get('aemetStationId')?.value;
    if (!selectedId) {
      return;
    }
    const station = this.stationByIdema.get(String(selectedId));
    if (station) {
      this.selectedStationLabel = this.formatStationLabel(station);
    }
  }

  public shouldShowSelectedEtoAemetPlaceholder(): boolean {
    const selectedId = this.form.get('etoAemetStationId')?.value;
    if (!selectedId || !this.selectedEtoAemetLabel) {
      return false;
    }
    return !this.stations.some(st => String(st.idema) === String(selectedId));
  }

  private syncSelectedEtoAemetLabel(): void {
    const selectedId = this.form.get('etoAemetStationId')?.value;
    if (!selectedId) {
      return;
    }
    const station = this.stationByIdema.get(String(selectedId));
    if (station) {
      this.selectedEtoAemetLabel = this.formatStationLabel(station);
    }
  }

  public shouldShowSelectedSiarStationPlaceholder(): boolean {
    const selectedId = this.form.get('siarStationId')?.value;
    if (!selectedId || !this.selectedSiarStationLabel) {
      return false;
    }
    return !this.siarStations.some(st => String(st.id) === String(selectedId));
  }

  public formatSiarStationLabel(station: SiarStation): string {
    if (!station) return '';
    const id =
      station.id !== undefined && station.id !== null ? String(station.id) : '';
    const name = station.name ? String(station.name) : '';
    if (id && name) {
      return `${id} - ${name}`;
    }
    return id || name;
  }

  private syncSelectedSiarStationLabel(): void {
    const selectedId = this.form.get('siarStationId')?.value;
    if (!selectedId) {
      return;
    }
    const station = this.siarStationById.get(String(selectedId));
    if (station) {
      this.selectedSiarStationLabel = this.formatSiarStationLabel(station);
    }
  }

  public shouldShowSelectedEtoSiarPlaceholder(): boolean {
    const selectedId = this.form.get('etoSiarStationId')?.value;
    if (!selectedId || !this.selectedEtoSiarLabel) {
      return false;
    }
    return !this.siarStations.some(st => String(st.id) === String(selectedId));
  }

  private syncSelectedEtoSiarLabel(): void {
    const selectedId = this.form.get('etoSiarStationId')?.value;
    if (!selectedId) {
      return;
    }
    const station = this.siarStationById.get(String(selectedId));
    if (station) {
      this.selectedEtoSiarLabel = this.formatSiarStationLabel(station);
    }
  }

  private enforceSourceAvailability(): void {
    const source = this.normalizePrecipitationSource(
      this.form.get('precipitationSource')?.value
    );
    if (source === 'pluviometer' && !this.hasPluviometer) {
      this.form.patchValue(
        { precipitationSource: 'open_meteo' },
        { emitEvent: false }
      );
      this.updatePrecipStationValidator('open_meteo');
    }
  }

  private getWeeklyReportObservable(moduleId: number) {
    const start = this.formatDate(this.rangeForm.get('start')?.value);
    const end = this.formatDate(this.rangeForm.get('end')?.value);
    if (!start || !end) {
      return of(null);
    }
    return this.waterBalanceService
      .getWeeklyReport(moduleId, start, end, this.etoDebug)
      .pipe(catchError(() => of(null)));
  }

  private getModuleId(value: any): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return Number(value) || 0;
    if (typeof value === 'object') {
      return (
        Number(
          value.idModule ||
            value.idModulo ||
            value.id ||
            value.moduleId ||
            value?.module?.idModule
        ) || 0
      );
    }
    return 0;
  }

  private filterModulesWithHu(modules: any[], sensors: any[]): any[] {
    const huModuleIds = new Set<number>();
    (sensors || []).forEach(sensor => {
      if (!this.isHumiditySensor(sensor)) {
        return;
      }
      const moduleId = this.getModuleId(sensor?.idModule);
      if (moduleId) {
        huModuleIds.add(moduleId);
      }
    });

    return (modules || [])
      .filter(mod => huModuleIds.has(this.getModuleId(mod)))
      .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
  }

  private isHumiditySensor(sensor: any): boolean {
    const sensorType = String(sensor?.sensorType || sensor?.type || '').toUpperCase();
    return (
      sensorType === 'HU' ||
      sensorType === 'E8' ||
      sensorType === 'SH' ||
      sensorType === 'SC'
    );
  }

  private isAdmin(): boolean {
    return this.roleId === 1;
  }

  private formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = ('0' + (d.getMonth() + 1)).slice(-2);
    const dd = ('0' + d.getDate()).slice(-2);
    return `${yyyy}-${mm}-${dd}`;
  }
}
